import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import Layout from '../components/Layout';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch,
  FiPackage, FiGrid, FiLink, FiLayers, FiChevronRight,
  FiAlertCircle, FiRefreshCw, FiUsers, FiPhone, FiMail, FiBriefcase,
  FiUser, FiUpload, FiFile, FiCreditCard, FiCheck, FiDownload,
} from 'react-icons/fi';
import { api } from '@crm/shared';
import { toast } from 'react-toastify';
import Pagination from '../components/Pagination';

/* ─── Design tokens ─── */
const C = {
  navy:'#0D3B5E', navyD:'#082B47', navyL:'#EBF4FF', navyM:'#1A5276',
  gold:'#F5A623', goldL:'#FEF3DC',
  green:'#16A34A', greenL:'#DCFCE7', greenB:'#86EFAC',
  red:'#DC2626', redL:'#FEF2F2', redB:'#FECACA',
  sky:'#0284C7', skyL:'#DBEAFE',
  purple:'#7C3AED', purpleL:'#EDE9FE',
  teal:'#0D9488', tealL:'#CCFBF1', tealB:'#5EEAD4',
  border:'#E5E7EB', borderL:'#F3F4F6', bg:'#F4F6F9',
  white:'#FFFFFF', t1:'#111827', t2:'#374151', t3:'#6B7280', t4:'#9CA3AF',
};

/* ─── Tabs ─── */
const TABS = [
  { id:'products',     label:'Products',     icon:<FiPackage size={13}/>, color:C.navy,    bg:C.navyL,    activeBg:C.navy,    shadow:'rgba(13,59,94,0.28)'   },
  { id:'institutions', label:'Institutions', icon:<FiGrid size={13}/>,   color:C.sky,     bg:C.skyL,     activeBg:'#0369A1', shadow:'rgba(3,105,161,0.28)'  },
  { id:'channels',     label:'Channels',     icon:<FiLink size={13}/>,   color:C.green,   bg:C.greenL,   activeBg:'#15803D', shadow:'rgba(21,128,61,0.28)'  },
  // { id:'lead_status',  label:'Lead Status',  icon:<FiLayers size={13}/>, color:'#C2410C', bg:'#FFF7ED',  activeBg:'#C2410C', shadow:'rgba(194,65,12,0.28)'  },
  { id:'bankers',      label:'Bankers',      icon:<FiUsers size={13}/>,  color:'#6D28D9', bg:'#EDE9FE',  activeBg:'#6D28D9', shadow:'rgba(109,40,217,0.28)' },
  { id:'bsa',          label:'BSA',          icon:<FiBriefcase size={13}/>, color:'#0D9488', bg:'#CCFBF1',  activeBg:'#0D9488', shadow:'rgba(13,148,136,0.28)' },
];

/* ─── Field configs (for non-banker tabs) ─── */
const FIELD_CONFIG = {
  products: [
    { key:'name',      label:'Product Name', required:true, span:2 },
    { key:'is_active', label:'Active',       required:false, type:'checkbox' },
  ],
  institutions: [
    { key:'name',             label:'Institution Name', required:true, span:2 },
    { key:'code',             label:'Code',             required:false },
    { key:'institution_type', label:'Institution Type', required:false, type:'select', options:[
      { value:'BANK',    label:'Bank'                    },
      { value:'NBFC',    label:'NBFC'                    },
      { value:'HFC',     label:'Housing Finance Company' },
      { value:'SFB',     label:'Small Finance Bank'      },
      { value:'FINTECH', label:'Fintech'                 },
      { value:'OTHER',   label:'Other'                   },
    ]},
    { key:'payout_rate', label:'Payout Rate %', required:false, type:'number' },
    { key:'is_active', label:'Active', required:false, type:'checkbox' },
  ],
  channels: [
    { key:'code',            label:'Channel Code',    required:true  },
    { key:'name',            label:'Channel Name',    required:true, span:2 },
    { key:'contact_person',  label:'Contact Person',  required:false },
    { key:'email',           label:'Email ID',        required:false, type:'email' },
    { key:'gstin',           label:'GSTIN',           required:false },
    { key:'payout_percent',  label:'Payout %',        required:false, type:'number' },
    { key:'billing_address', label:'Billing Address', required:false, span:2 },
  ],
};

const API_MAP = {
  products:     { get:'getProducts',     create:'createProduct',     update:'updateProduct',     del:'deleteProduct'     },
  institutions: { get:'getInstitutions', create:'createInstitution', update:'updateInstitution', del:'deleteInstitution' },
  channels:     { get:'getChannels',     create:'createChannel',     update:'updateChannel',     del:'deleteChannel'     },
};

const emptyForm = (tab) => (FIELD_CONFIG[tab]||[]).reduce((acc,f) => ({ ...acc, [f.key]:'' }), {});

/* ─── Institution type helpers ─── */
const TYPE_LABELS = { BANK:'Bank', NBFC:'NBFC', HFC:'Housing Finance Co.', SFB:'Small Finance Bank', FINTECH:'Fintech', OTHER:'Other' };
const TYPE_COLORS = {
  BANK:    { bg:C.skyL,    color:C.sky     },
  NBFC:    { bg:C.goldL,   color:'#92400E' },
  HFC:     { bg:C.greenL,  color:C.green   },
  SFB:     { bg:'#FCE7F3', color:'#9D174D' },
  FINTECH: { bg:C.purpleL, color:C.purple  },
  OTHER:   { bg:'#F1F5F9', color:C.t2      },
};

/* ─── Lead Status colors ─── */
const PARENT_COLORS = [
  { bg:C.purpleL, color:C.purple, border:'#C4B5FD' },
  { bg:C.skyL,    color:C.sky,    border:'#BAE6FD'  },
  { bg:'#FCE7F3', color:'#9D174D',border:'#F9A8D4'  },
  { bg:C.greenL,  color:C.green,  border:C.greenB   },
  { bg:C.goldL,   color:'#92400E',border:'#FCD34D'  },
];

/* ─── Shared input style ─── */
const inputSt = (hasErr=false) => ({
  width:'100%', height:40, border:`1.5px solid ${hasErr?C.red:C.border}`, borderRadius:9,
  padding:'0 12px', fontSize:13, color:C.t1, background:hasErr?'#FFF9F9':'#FAFBFC',
  outline:'none', fontFamily:"'Plus Jakarta Sans',sans-serif", boxSizing:'border-box',
  transition:'all 0.15s', appearance:'none', WebkitAppearance:'none',
});
const onFocus = (e) => { e.target.style.borderColor=C.navy; e.target.style.background='#fff'; e.target.style.boxShadow='0 0 0 3px rgba(13,59,94,0.08)'; };
const onBlur  = (e) => { e.target.style.borderColor=C.border; e.target.style.background='#FAFBFC'; e.target.style.boxShadow='none'; };

/* ─── Shimmer ─── */
const Sh = ({ h=20, w='100%', r=6 }) => (
  <div style={{ height:h, width:w, borderRadius:r, background:'linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)', backgroundSize:'400px 100%', animation:'cfShimmer 1.4s infinite' }}/>
);

/* ─── Spinner ─── */
const Spin = () => (
  <div style={{ width:14, height:14, borderRadius:'50%', border:'2px solid rgba(255,255,255,0.35)', borderTopColor:'#fff', animation:'cfSpin 0.8s linear infinite' }}/>
);

/* ═══════════════════════════════════════════════════════
   BANKER DRAWER
═══════════════════════════════════════════════════════ */
/* Stable input style objects — defined outside component to avoid re-creation on every render */
const BKR_INPUT_ST = {
  width:'100%', height:40, border:`1.5px solid #E5E7EB`, borderRadius:9,
  padding:'0 12px', fontSize:13, color:'#111827', background:'#FAFBFC',
  outline:'none', fontFamily:"'Plus Jakarta Sans',sans-serif", boxSizing:'border-box',
  transition:'all 0.15s', appearance:'none', WebkitAppearance:'none',
};
const BKR_INPUT_ERR = {
  ...BKR_INPUT_ST, border:'1.5px solid #DC2626', background:'#FFF9F9',
};
const bkrFocus = (e) => { e.target.style.borderColor='#0D3B5E'; e.target.style.background='#fff'; e.target.style.boxShadow='0 0 0 3px rgba(13,59,94,0.08)'; };
const bkrBlur  = (e) => { e.target.style.borderColor='#E5E7EB'; e.target.style.background='#FAFBFC'; e.target.style.boxShadow='none'; };

function BankerDrawer({ institutions, editingBanker, onClose, onSaved }) {
  const isEdit = !!(editingBanker?.id);
  const [form, setForm] = useState({
    name:           editingBanker?.name           || '',
    designation:    editingBanker?.designation    || '',
    contact_number: editingBanker?.contact_number || '',
    email:          editingBanker?.email          || '',
    institution:    String(editingBanker?.institution ?? ''),
    is_active:      editingBanker?.is_active      ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  const validate = () => {
    const e = {};
    if (!form.name.trim())           e.name           = 'Required';
    if (!form.institution)           e.institution    = 'Required';
    const phone = form.contact_number.trim();
    if (!phone)                                    e.contact_number = 'Required';
    else if (!/^\d{10}$/.test(phone))              e.contact_number = 'Enter valid 10-digit number';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Enter valid email address';
    setErrors(e); return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const payload = {
        name:           form.name.trim(),
        designation:    form.designation.trim() || '',
        contact_number: form.contact_number.trim(),
        email:          form.email.trim() || '',
        institution:    parseInt(form.institution),
        is_active:      !!form.is_active,
        updated_by:     user?.id || null,
      };
      if (isEdit) {
        await api.updateBanker(editingBanker.id, payload);
        toast.success('Banker updated');
      } else {
        await api.createBanker({ ...payload, created_by: user?.id || null });
        toast.success('Banker added');
      }
      onSaved();
    } catch (err) {
      const data = err?.response?.data || {};
      if (typeof data === 'object' && Object.keys(data).length) {
        const mapped = {};
        Object.entries(data).forEach(([k,v]) => { mapped[k] = Array.isArray(v) ? v[0] : v; });
        setErrors(mapped); toast.error('Please fix the errors below');
      } else { toast.error('Failed to save banker'); }
    } finally { setSaving(false); }
  };

  return ReactDOM.createPortal(
    <div style={{ position:'fixed', inset:0, zIndex:2000 }}>
      <div style={{ position:'absolute', inset:0, background:'rgba(13,27,42,0.45)', backdropFilter:'blur(3px)' }} onClick={onClose}/>
      <div className="ms-drawer" style={{ position:'absolute', top:0, right:0, bottom:0, width:500, background:C.white, boxShadow:'-8px 0 40px rgba(13,27,42,0.20)', display:'flex', flexDirection:'column', overflow:'hidden', animation:'cfSlideIn 0.24s ease both' }}>

        {/* Header - same as other tabs */}
        <div style={{ padding:'18px 24px', background:'linear-gradient(120deg,#0D3B5E,#1A5276)', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          <div>
            <div style={{ fontSize:10.5, fontWeight:700, color:'rgba(255,255,255,0.50)', marginBottom:3, textTransform:'uppercase', letterSpacing:'0.08em' }}>Bankers</div>
            <div style={{ fontSize:17, fontWeight:800, color:'#fff', letterSpacing:'-0.02em' }}>
              {isEdit ? 'Edit Banker' : 'Add Banker'}
              {isEdit && <span style={{ fontSize:11, fontWeight:400, opacity:0.55, marginLeft:7 }}>#{editingBanker.id}</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ width:36, height:36, borderRadius:9, background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.20)', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <FiX size={17}/>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ flex:1, display:'flex', flexDirection:'column', minHeight:0 }}>
          <div className="ms-drawer-grid" style={{ overflowY:'auto', padding:'24px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'18px 16px' }}>

            <FieldWrap label="Institution" req err={errors.institution} span>
              <div style={{ position:'relative' }}>
                <select value={form.institution} onChange={e=>setForm(p=>({...p,institution:e.target.value}))}
                  style={{ ...(errors.institution ? BKR_INPUT_ERR : BKR_INPUT_ST), cursor:'pointer', paddingRight:26 }}
                  onFocus={bkrFocus} onBlur={bkrBlur}>
                  <option value="">— Select Institution —</option>
                  {institutions.map(i => <option key={i.id} value={String(i.id)}>{i.name}</option>)}
                </select>
                <span style={{ position:'absolute', right:9, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', color:C.t4, fontSize:10 }}>&#9662;</span>
              </div>
            </FieldWrap>

            <FieldWrap label="Banker Name" req err={errors.name}>
              <input type="text" value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))}
                placeholder="e.g. Rahul Sharma" style={errors.name ? BKR_INPUT_ERR : BKR_INPUT_ST} onFocus={bkrFocus} onBlur={bkrBlur}/>
            </FieldWrap>

            <FieldWrap label="Designation" err={errors.designation}>
              <input type="text" value={form.designation} onChange={e=>setForm(p=>({...p,designation:e.target.value}))}
                placeholder="e.g. Relationship Manager" style={BKR_INPUT_ST} onFocus={bkrFocus} onBlur={bkrBlur}/>
            </FieldWrap>

            <FieldWrap label="Contact Number" req err={errors.contact_number}>
              <input type="text" value={form.contact_number}
                onChange={e => { const v = e.target.value.replace(/[^0-9]/g, ''); setForm(p=>({...p,contact_number:v})); }}
                placeholder="10-digit mobile number" maxLength={10} style={errors.contact_number ? BKR_INPUT_ERR : BKR_INPUT_ST} onFocus={bkrFocus} onBlur={bkrBlur}/>
            </FieldWrap>

            <FieldWrap label="Email ID" err={errors.email}>
              <input type="email" value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))}
                placeholder="banker@bank.com" style={errors.email ? BKR_INPUT_ERR : BKR_INPUT_ST} onFocus={bkrFocus} onBlur={bkrBlur}/>
            </FieldWrap>

            <FieldWrap span>
              <div style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 12px', background:C.bg, borderRadius:8, border:`1px solid ${C.border}` }}>
                <input type="checkbox" id="bkr_active" checked={!!form.is_active}
                  onChange={e=>setForm(p=>({...p,is_active:e.target.checked}))}
                  style={{ width:15, height:15, cursor:'pointer', accentColor:C.purple }}/>
                <label htmlFor="bkr_active" style={{ fontSize:13, fontWeight:600, color:C.t2, cursor:'pointer' }}>Active</label>
              </div>
            </FieldWrap>

          </div>

          {/* Footer - same as other tabs */}
          <div style={{ padding:'16px 24px', borderTop:`1px solid ${C.borderL}`, display:'flex', gap:10, justifyContent:'flex-end', background:'#FAFBFC', flexShrink:0 }}>
            <button type="button" onClick={onClose} disabled={saving}
              style={{ height:40, padding:'0 22px', border:`1px solid ${C.border}`, borderRadius:9, background:C.white, color:C.t2, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
            <button type="submit" disabled={saving}
              style={{ height:40, padding:'0 28px', minWidth:140, background:saving?C.navyM:C.gold, color:saving?'#fff':'#0D1B2A', border:'none', borderRadius:9, fontSize:13, fontWeight:800, cursor:saving?'not-allowed':'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:7, boxShadow:'0 2px 10px rgba(245,166,35,0.38)' }}>
              {saving ? <><Spin/> Saving...</> : <><FiSave size={14}/> {isEdit ? 'Update' : 'Add Banker'}</>}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════════
   BANKERS TAB — flat table, same pattern as other tabs
═══════════════════════════════════════════════════════ */
function BankersTab({ institutions, openDrawerTrigger }) {
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState('');
  const [filterInst,    setFilterInst]    = useState('');
  const [showDrawer,    setShowDrawer]    = useState(false);
  const [editingBanker, setEditingBanker] = useState(null);
  const [deleting,      setDeleting]      = useState(null);
  const [confirmDel,    setConfirmDel]    = useState(null);
  const [allBankers,  setAllBankers]  = useState([]);
  const [totalCount,  setTotalCount]  = useState(0);
  const [page,        setPage]        = useState(1);
  const PAGE_SIZE = 15;

  useEffect(() => {
    if (openDrawerTrigger > 0) { setEditingBanker(null); setShowDrawer(true); }
  }, [openDrawerTrigger]);

  const fetchBankers = useCallback(async (pg = 1, inst = '') => {
    setLoading(true);
    try {
      const params = { page: pg, page_size: PAGE_SIZE };
      if (inst) params.institution = inst;
      const res     = await api.getBankers(params);
      const data    = res?.data;
      const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
      setAllBankers(results);
      setTotalCount(data?.count || results.length);
      setPage(pg);
    } catch { toast.error('Failed to load bankers'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBankers(1, ''); }, [fetchBankers]);

  /* client-side filter — institution (backend may ignore it) + search */
  const bankers = allBankers.filter(b => {
    const matchInst   = !filterInst || String(b.institution) === String(filterInst);
    const s           = search.trim().toLowerCase();
    const matchSearch = !s || (
      b.name?.toLowerCase().includes(s) ||
      b.designation?.toLowerCase().includes(s) ||
      b.contact_number?.toLowerCase().includes(s) ||
      b.email?.toLowerCase().includes(s)
    );
    return matchInst && matchSearch;
  });

  const handleDelete = async (banker) => {
    setDeleting(banker.id); setConfirmDel(null);
    try {
      await api.deleteBanker(banker.id);
      toast.success('Banker deleted');
      fetchBankers(page, filterInst);
    } catch { toast.error('Failed to delete banker'); }
    finally { setDeleting(null); }
  };

  const instName = (id) => institutions.find(i => String(i.id) === String(id))?.name || '—';

  return (
    <>
      <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, boxShadow:'0 2px 6px rgba(13,59,94,0.06)', overflow:'hidden' }}>

        {/* Toolbar */}
        <div style={{ padding:'11px 16px', borderBottom:`1px solid ${C.borderL}`, display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <FiUsers size={13} style={{ color:C.purple, opacity:0.8 }}/>
            <span style={{ fontSize:13, fontWeight:700, color:C.t1 }}>Bankers</span>
            {!loading && <span style={{ background:C.navyL, color:C.navy, padding:'2px 8px', borderRadius:20, fontSize:10.5, fontWeight:700 }}>{search.trim() ? bankers.length : totalCount}</span>}
          </div>
          <div style={{ position:'relative', flex:1, minWidth:160 }}>
            <FiSearch size={13} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:C.t4, pointerEvents:'none' }}/>
            <input type="text" placeholder="Search name, designation..." value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if(e.key === 'Enter') fetchBankers(1, filterInst); }}
              style={{ ...inputSt(), height:36, paddingLeft:32 }} onFocus={onFocus} onBlur={onBlur}/>
          </div>
          <div style={{ position:'relative', minWidth:180 }}>
            <select value={filterInst} onChange={e => { setFilterInst(e.target.value); setSearch(''); fetchBankers(1, e.target.value); }}
              style={{ ...inputSt(), height:36, cursor:'pointer', paddingRight:26 }} onFocus={onFocus} onBlur={onBlur}>
              <option value="">All Institutions</option>
              {institutions.map(i => <option key={i.id} value={String(i.id)}>{i.name}</option>)}
            </select>
            <span style={{ position:'absolute', right:9, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', color:C.t4, fontSize:10 }}>&#9662;</span>
          </div>
          <button onClick={() => fetchBankers(1, filterInst)}
            style={{ height:36, padding:'0 14px', borderRadius:9, background:C.navy, border:'none', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:5 }}>
            <FiSearch size={12}/> Search
          </button>
          <button onClick={() => { setSearch(''); setFilterInst(''); fetchBankers(1, ''); }} title="Reset"
            style={{ width:36, height:36, borderRadius:9, background:C.navyL, border:`1px solid ${C.border}`, color:C.navy, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <FiRefreshCw size={13}/>
          </button>
        </div>

        {loading && <div style={{ padding:16, display:'flex', flexDirection:'column', gap:10 }}>{[1,2,3,4,5].map(i=><Sh key={i} h={48} r={8}/>)}</div>}

        {!loading && allBankers.length === 0 && (
          <div style={{ padding:'48px 24px', textAlign:'center' }}>
            <div style={{ width:46, height:46, borderRadius:'50%', background:C.purpleL, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px' }}>
              <FiUsers size={19} style={{ color:C.purple }}/>
            </div>
            <div style={{ fontSize:14, fontWeight:700, color:C.t1, marginBottom:5 }}>No bankers yet</div>
            <div style={{ fontSize:12.5, color:C.t4 }}>Click "Add Banker" above to get started</div>
          </div>
        )}
        {!loading && allBankers.length > 0 && bankers.length === 0 && search.trim() && (
          <div style={{ padding:'32px 24px', textAlign:'center' }}>
            <div style={{ fontSize:13, fontWeight:600, color:C.t3 }}>No results for "{search}"</div>
            <div style={{ fontSize:12, color:C.t4, marginTop:4 }}>Try a different keyword</div>
          </div>
        )}

        {!loading && bankers.length > 0 && (
          <>
            {/* Desktop table */}
            <div className="ms-bk-table">
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', minWidth:580 }}>
                  <thead>
                    <tr style={{ background:'#F7F9FB' }}>
                      {['#','Name','Designation','Institution','Contact','Active'].map((col,i) => (
                        <th key={col} style={{ padding:'10px 14px', fontSize:10.5, fontWeight:800, color:C.t1, textTransform:'uppercase', letterSpacing:'0.08em', textAlign:'left', borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap', width:i===0?36:'auto' }}>{col}</th>
                      ))}
                      <th style={{ padding:'10px 14px', fontSize:10.5, fontWeight:800, color:C.t1, textTransform:'uppercase', letterSpacing:'0.08em', textAlign:'right', borderBottom:`1px solid ${C.border}`, width:84 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bankers.map((bk, idx) => {
                      const isLast = idx === bankers.length - 1;
                      const isDel  = deleting === bk.id;
                      return (
                        <tr key={bk.id} className="ms-tr" style={{ background:C.white, opacity:isDel?0.5:1, transition:'all 0.12s' }}>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t4 }}>{(page-1)*PAGE_SIZE+idx+1}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:13, fontWeight:600, color:C.t1 }}>{bk.name}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12.5, color:C.t3 }}>{bk.designation || <span style={{ color:C.t4 }}>—</span>}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
                            <span style={{ background:C.navyL, color:C.navy, padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:600, whiteSpace:'nowrap' }}>{instName(bk.institution)}</span>
                          </td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t3, fontFamily:'monospace' }}>{bk.contact_number || <span style={{ color:C.t4 }}>—</span>}</td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
                            {bk.is_active !== false
                              ? <span style={{ background:C.greenL, color:C.green, border:`1px solid ${C.greenB}`, padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700 }}>Yes</span>
                              : <span style={{ background:C.redL,   color:C.red,   border:`1px solid ${C.redB}`,   padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700 }}>No</span>}
                          </td>
                          <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>
                            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                              <button className="ms-btn" title="Edit" disabled={isDel}
                                onClick={() => { setEditingBanker(bk); setShowDrawer(true); }}
                                style={{ color:C.navy, borderColor:C.border }}><FiEdit2 size={13}/></button>
                              <button className="ms-btn" title="Delete" disabled={isDel}
                                onClick={() => setConfirmDel(bk)}
                                style={{ color:isDel?C.t4:C.red, borderColor:C.redB }}>
                                {isDel
                                  ? <div style={{ width:12, height:12, borderRadius:'50%', border:`2px solid ${C.red}`, borderTopColor:'transparent', animation:'cfSpin 0.8s linear infinite' }}/>
                                  : <FiTrash2 size={13}/>}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Mobile cards */}
            <div className="ms-bk-cards" style={{ display:'none', padding:'10px', flexDirection:'column', gap:8 }}>
              {bankers.map((bk, idx) => {
                const isDel = deleting === bk.id;
                return (
                  <div key={bk.id} style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:10, padding:'12px 14px', opacity:isDel?0.5:1, transition:'all 0.12s' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                      <div>
                        <div style={{ fontSize:13.5, fontWeight:700, color:C.t1 }}>{bk.name}</div>
                        <div style={{ fontSize:12, color:C.t3, marginTop:2 }}>{bk.designation || '—'}</div>
                      </div>
                      <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                        <button className="ms-btn" title="Edit" disabled={isDel}
                          onClick={() => { setEditingBanker(bk); setShowDrawer(true); }}
                          style={{ color:C.navy, borderColor:C.border }}><FiEdit2 size={14}/></button>
                        <button className="ms-btn" title="Delete" disabled={isDel}
                          onClick={() => setConfirmDel(bk)}
                          style={{ color:isDel?C.t4:C.red, borderColor:C.redB }}>
                          {isDel
                            ? <div style={{ width:12, height:12, borderRadius:'50%', border:`2px solid ${C.red}`, borderTopColor:'transparent', animation:'cfSpin 0.8s linear infinite' }}/>
                            : <FiTrash2 size={14}/>}
                        </button>
                      </div>
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12 }}>
                        <span style={{ color:C.t4, minWidth:80 }}>Institution</span>
                        <span style={{ background:C.navyL, color:C.navy, padding:'1px 8px', borderRadius:20, fontSize:11, fontWeight:600 }}>{instName(bk.institution)}</span>
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12 }}>
                        <span style={{ color:C.t4, minWidth:80 }}>Contact</span>
                        <span style={{ color:C.t2, fontFamily:'monospace' }}>{bk.contact_number || '—'}</span>
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12 }}>
                        <span style={{ color:C.t4, minWidth:80 }}>Email</span>
                        <span style={{ color:C.t2 }}>{bk.email || '—'}</span>
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12 }}>
                        <span style={{ color:C.t4, minWidth:80 }}>Active</span>
                        {bk.is_active !== false
                          ? <span style={{ background:C.greenL, color:C.green, border:`1px solid ${C.greenB}`, padding:'1px 8px', borderRadius:20, fontSize:11, fontWeight:700 }}>Yes</span>
                          : <span style={{ background:C.redL, color:C.red, border:`1px solid ${C.redB}`, padding:'1px 8px', borderRadius:20, fontSize:11, fontWeight:700 }}>No</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ borderTop:`1px solid ${C.borderL}` }}>
              <Pagination currentPage={page} totalCount={search.trim() ? bankers.length : totalCount} pageSize={PAGE_SIZE} onPageChange={p => fetchBankers(p, filterInst)}/>
            </div>
          </>
        )}
      </div>

      {showDrawer && (
        <BankerDrawer
          institutions={institutions}
          editingBanker={editingBanker}
          onClose={() => { setShowDrawer(false); setEditingBanker(null); }}
          onSaved={() => { setShowDrawer(false); setEditingBanker(null); fetchBankers(page, filterInst); }}
        />
      )}

      {confirmDel && (
        <div style={{ position:'fixed', inset:0, background:'rgba(13,27,42,0.55)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2100, padding:16 }}
          onClick={e=>{ if(e.target===e.currentTarget) setConfirmDel(null); }}>
          <div style={{ background:C.white, borderRadius:14, width:'100%', maxWidth:380, boxShadow:'0 24px 60px rgba(13,27,42,0.30)', overflow:'hidden', animation:'cfFadeUp 0.18s ease both' }}>
            <div style={{ padding:'15px 20px', borderBottom:`1px solid ${C.borderL}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontWeight:800, fontSize:14, color:C.t1 }}>Confirm Delete</span>
              <button onClick={()=>setConfirmDel(null)} style={{ background:'none', border:'none', cursor:'pointer', color:C.t4, display:'flex' }}><FiX size={16}/></button>
            </div>
            <div style={{ padding:'20px', textAlign:'center' }}>
              <div style={{ width:46, height:46, borderRadius:'50%', background:C.redL, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px' }}>
                <FiTrash2 size={19} style={{ color:C.red }}/>
              </div>
              <p style={{ fontSize:13.5, color:C.t2, margin:'0 0 4px', lineHeight:1.5 }}>Delete <strong>"{confirmDel.name}"</strong>?</p>
              <p style={{ fontSize:12, color:C.t4, margin:0 }}>This action cannot be undone.</p>
            </div>
            <div style={{ padding:'12px 20px', borderTop:`1px solid ${C.borderL}`, display:'flex', gap:10, justifyContent:'flex-end', background:'#FAFBFC' }}>
              <button onClick={()=>setConfirmDel(null)} style={{ height:36, padding:'0 18px', border:`1px solid ${C.border}`, borderRadius:8, background:C.white, color:C.t2, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
              <button onClick={()=>handleDelete(confirmDel)} style={{ height:36, padding:'0 20px', background:C.red, color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:6 }}>
                <FiTrash2 size={13}/> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════
   BSA LOOKUP TABLES
═══════════════════════════════════════════════════════ */
const BSA_TYPES = [
  { value:'BROKER',                label:'Broker'               },
  { value:'CA',                    label:'CA / CS'              },
  { value:'ARCHITECT',             label:'Architect'            },
  { value:'FINANCIAL_CONSULTANT',  label:'Financial Consultant' },
  { value:'CORPORATE_FUNCTIONARY', label:'Corporate Functionary'},
  { value:'BUILDER',               label:'Builder'              },
  { value:'OTHER',                 label:'Other'                },
];
const CONSTITUTIONS = [
  { value:'INDIVIDUAL',     label:'Individual'    },
  { value:'PARTNERSHIP',    label:'Partnership'   },
  { value:'PROPRIETORSHIP', label:'Proprietorship'},
  { value:'COMPANY',        label:'Company'       },
];
const BSA_TYPE_COLORS = {
  BROKER:               { bg:C.skyL,    color:C.sky,     border:'#BAE6FD' },
  CA:                   { bg:C.purpleL, color:C.purple,  border:'#C4B5FD' },
  ARCHITECT:            { bg:'#FFF7ED', color:'#C2410C', border:'#FED7AA' },
  FINANCIAL_CONSULTANT: { bg:C.navyL,   color:C.navy,    border:'#BFDBFE' },
  CORPORATE_FUNCTIONARY:{ bg:C.goldL,   color:'#92400E', border:'#FDE68A' },
  BUILDER:              { bg:C.greenL,  color:C.green,   border:C.greenB  },
  OTHER:                { bg:'#F1F5F9', color:C.t2,      border:C.border  },
};

const BSA_INPUT = {
  width:'100%', height:40, border:'1.5px solid #E5E7EB', borderRadius:9,
  padding:'0 12px', fontSize:13, color:'#111827', background:'#FAFBFC',
  outline:'none', fontFamily:"'Plus Jakarta Sans',sans-serif", boxSizing:'border-box',
  transition:'all 0.15s', appearance:'none', WebkitAppearance:'none',
};
const BSA_INPUT_ERR = { ...BSA_INPUT, border:'1.5px solid #DC2626', background:'#FFF9F9' };

/* ─── Shared field wrapper — defined at module level to prevent focus loss on re-render ─── */
const FieldWrap = ({ label, req, err, children, span }) => (
  <div style={{ gridColumn: span ? '1/-1' : 'auto' }}>
    <label style={{ display:'block', fontSize:11.5, fontWeight:700, color:C.t2, marginBottom:5 }}>
      {label}{req && <span style={{ color:C.red, marginLeft:2 }}>*</span>}
    </label>
    {children}
    {err && <div style={{ fontSize:11, color:C.red, marginTop:3 }}>{err}</div>}
  </div>
);

/* ─── BSA select helper — module level so it doesn't remount on every render ─── */
const BsaSel = ({ field, opts, err, form, setForm }) => (
  <div style={{ position:'relative' }}>
    <select value={form[field]} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}
      style={{ ...(err ? BSA_INPUT_ERR : BSA_INPUT), cursor:'pointer', paddingRight:26 }}>
      <option value="">— Select —</option>
      {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
    <span style={{ position:'absolute', right:9, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', color:C.t4, fontSize:10 }}>&#9662;</span>
  </div>
);

/* ─── BSA Drawer ─── */
function BsaDrawer({ editingBsa, onClose, onSaved }) {
  const isEdit = !!(editingBsa?.id);
  const disbPay = Number(editingBsa?.disbursement_payout_percentage) || 0;
  const compPay = Number(editingBsa?.company_payout_percentage) || 0;
  const [form, setForm] = useState({
    name:                editingBsa?.name                || '',
    address:             editingBsa?.address             || '',
    pan:                 editingBsa?.pan                 || '',
    gst_number:          editingBsa?.gst_number          || '',
    constitution:        editingBsa?.constitution        || '',
    bsa_type:            editingBsa?.bsa_type            || '',
    payout_type:        disbPay > 0 ? 'disbursement' : (compPay > 0 ? 'company' : 'disbursement'),
    payout_percentage: disbPay > 0 ? String(parseFloat(editingBsa.disbursement_payout_percentage)) : (compPay > 0 ? String(parseFloat(editingBsa.company_payout_percentage)) : ''),
    bank_name:           editingBsa?.bank_name           || '',
    account_number:      editingBsa?.account_number      || '',
    ifsc_code:           editingBsa?.ifsc_code           || '',
    account_holder_name: editingBsa?.account_holder_name || '',
    mobile_number:       editingBsa?.mobile_number       || '',
    email:               editingBsa?.email               || '',
    is_active:           editingBsa?.is_active           ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [step,    setStep]    = useState(1);
  const [maxStep, setMaxStep] = useState(isEdit ? 3 : 1); // edit mode: all steps unlocked

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docType, setDocType] = useState('KYC_ID');
  const [docFile, setDocFile] = useState(null);
  const [docUploading, setDocUploading] = useState(false);
  const [docDeleting, setDocDeleting] = useState(null);
  const [pendingDocs, setPendingDocs] = useState([]); // queued docs for new BSA
  const fileRef = useRef(null);

  const DOC_TYPES = [
    { value:'KYC_ID', label:'KYC Identity Proof' },
    { value:'KYC_ADDRESS', label:'KYC Address Proof' },
    { value:'AGREEMENT', label:'Agreement Document' },
    { value:'BANK_CHEQUE', label:'Bank Cancelled Cheque' },
    { value:'BANK_STATEMENT', label:'Bank Statement' },
  ];

  useEffect(() => {
    if (isEdit && editingBsa?.id) {
      setDocsLoading(true);
      api.getBsaDocuments({ bsa: editingBsa.id })
        .then(res => {
          const arr = Array.isArray(res?.data?.results) ? res.data.results : Array.isArray(res?.data) ? res.data : [];
          setDocs(arr);
        })
        .catch(() => {})
        .finally(() => setDocsLoading(false));
    }
  }, [isEdit, editingBsa?.id]); // eslint-disable-line

  const handleDocUpload = async () => {
    if (!docFile) { toast.error('Please select a file'); return; }
    if (!isEdit || !editingBsa?.id) { toast.error('Save the BSA first, then upload documents'); return; }
    setDocUploading(true);
    try {
      const fd = new FormData();
      fd.append('bsa_id', editingBsa.id);
      fd.append('document_type', docType);
      fd.append('file', docFile);
      const res = await api.createBsaDocument(fd);
      setDocs(prev => [...prev, res.data]);
      setDocFile(null);
      if (fileRef.current) fileRef.current.value = '';
      toast.success('Document uploaded');
    } catch (err) { toast.error(err?.message || 'Upload failed'); }
    finally { setDocUploading(false); }
  };

  const handleDocDelete = async (docId) => {
    setDocDeleting(docId);
    try {
      await api.deleteBsaDocument(docId);
      setDocs(prev => prev.filter(d => d.id !== docId));
      toast.success('Document deleted');
    } catch (err) { toast.error(err?.message || 'Failed to delete document'); }
    finally { setDocDeleting(null); }
  };

  const docLabel = (v) => DOC_TYPES.find(d => d.value === v)?.label || v;
  const docFileName = (url) => { if (!url) return 'Document'; try { return decodeURIComponent(url.split('/').pop().split('?')[0]) || 'Document'; } catch { return 'Document'; } };

  const validateStep = (s) => {
    const e = {};
    if (s === 1 || s === 2 || s === 3) {
      if (!form.name.trim())                    e.name = 'Required';
      if (!form.address.trim())                 e.address = 'Required';
      if (!form.pan.trim())                     e.pan = 'Required';
      else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.pan.trim())) e.pan = 'Enter valid PAN (e.g. ABCDE1234F)';
      if (!form.bsa_type)                       e.bsa_type = 'Required';
      if (!form.constitution)                   e.constitution = 'Required';
      const mob = form.mobile_number.trim();
      if (!mob)                                 e.mobile_number = 'Required';
      else if (!/^\d{10}$/.test(mob))           e.mobile_number = 'Enter valid 10-digit number';
      if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Enter valid email address';
      const pay = Number(form.payout_percentage) || 0;
      if (!form.payout_type || pay === 0) e.payout_percentage = 'Required';
    }
    if (s === 2 || s === 3) {
      if (!form.bank_name.trim())               e.bank_name = 'Required';
      if (!form.account_number.trim())          e.account_number = 'Required';
      if (!form.ifsc_code.trim())               e.ifsc_code = 'Required';
      if (!form.account_holder_name.trim())     e.account_holder_name = 'Required';
    }
    setErrors(e); return Object.keys(e).length === 0;
  };

  const validate = () => validateStep(3);

  const goNext = (to) => {
    if (validateStep(step)) {
      setStep(to);
      setMaxStep(prev => Math.max(prev, to));
    }
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      // errors state is async — recompute which step has errors from form values
      const hasStep1Error = !form.name.trim() || !form.address.trim() || !form.pan.trim() || !form.bsa_type || !form.constitution || !form.mobile_number.trim() || (form.company_payout_percentage === '' || form.company_payout_percentage == null);
      const hasStep2Error = !form.bank_name.trim() || !form.account_number.trim() || !form.ifsc_code.trim() || !form.account_holder_name.trim();
      if (hasStep1Error) setStep(1);
      else if (hasStep2Error) setStep(2);
      return;
    }
    setSaving(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const payload = {
        name:                form.name.trim(),
        address:             form.address.trim() || '',
        pan:                 form.pan.trim() || '',
        gst_number:          form.gst_number.trim() || '',
        constitution:        form.constitution,
        bsa_type:            form.bsa_type,
        disbursement_payout_percentage: form.payout_type === 'disbursement' ? (form.payout_percentage || '0') : '0',
        company_payout_percentage:   form.payout_type === 'company'   ? (form.payout_percentage || '0') : '0',
        bank_name:           form.bank_name.trim() || '',
        account_number:      form.account_number.trim() || '',
        ifsc_code:           form.ifsc_code.trim() || '',
        account_holder_name: form.account_holder_name.trim() || '',
        mobile_number:       form.mobile_number.trim(),
        email:               form.email.trim() || '',
        is_active:           !!form.is_active,
        updated_by:          user?.id || null,
      };
      if (isEdit) {
        await api.updateBsa(editingBsa.id, payload);
        toast.success('BSA updated');
      } else {
        const res = await api.createBsa({ ...payload, created_by: user?.id || null });
        const newId = res?.data?.id;
        if (newId && pendingDocs.length > 0) {
          for (const pd of pendingDocs) {
            try {
              const fd = new FormData();
              fd.append('bsa_id', newId);
              fd.append('document_type', pd.type);
              fd.append('file', pd.file);
              await api.createBsaDocument(fd);
            } catch { /* skip failed doc, don't block save */ }
          }
          toast.success(`BSA created with ${pendingDocs.length} document${pendingDocs.length > 1 ? 's' : ''}`);
        } else {
          toast.success('BSA created');
        }
      }
      onSaved();
    } catch (err) {
      const data = err?.response?.data || err || {};
      if (typeof data === 'object' && Object.keys(data).length) {
        const mapped = {};
        Object.entries(data).forEach(([k,v]) => { mapped[k] = Array.isArray(v) ? v[0] : v; });
        setErrors(mapped); toast.error('Please fix the errors below');
      } else { toast.error('Failed to save BSA'); }
    } finally { setSaving(false); }
  };

  return ReactDOM.createPortal(
    <div style={{ position:'fixed', inset:0, zIndex:2000 }}>
      <div style={{ position:'absolute', inset:0, background:'rgba(13,27,42,0.45)', backdropFilter:'blur(3px)' }} onClick={onClose}/>
      <div className="ms-drawer" style={{ position:'absolute', top:0, right:0, bottom:0, width:520, background:C.white, boxShadow:'-8px 0 40px rgba(13,27,42,0.20)', display:'flex', flexDirection:'column', overflow:'hidden', animation:'cfSlideIn 0.24s ease both' }}>
        {/* Header */}
        <div style={{ padding:'18px 24px', background:'linear-gradient(120deg,#0D9488,#0F766E)', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          <div>
            <div style={{ fontSize:10.5, fontWeight:700, color:'rgba(255,255,255,0.50)', marginBottom:3, textTransform:'uppercase', letterSpacing:'0.08em' }}>BSA Master</div>
            <div style={{ fontSize:17, fontWeight:800, color:'#fff', letterSpacing:'-0.02em' }}>
              {isEdit ? 'Edit BSA' : 'Add BSA'}
              {isEdit && <span style={{ fontSize:11, fontWeight:400, opacity:0.55, marginLeft:7 }}>#{editingBsa.id}</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ width:36, height:36, borderRadius:9, background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.20)', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}><FiX size={17}/></button>
        </div>

        {/* Step tabs */}
        <div style={{ display:'flex', borderBottom:`1px solid ${C.border}`, background:'#FAFBFC', flexShrink:0 }}>
          {[{ n:1, lbl:'Basic Info' }, { n:2, lbl:'Bank Details' }, { n:3, lbl:'Documents' }].map(({ n, lbl }) => {
            const unlocked = n <= maxStep;
            return (
              <button key={n}
                onClick={() => { if (unlocked) setStep(n); }}
                disabled={!unlocked}
                style={{ flex:1, padding:'11px 0', border:'none', background:'none', cursor:unlocked?'pointer':'not-allowed', fontFamily:'inherit', fontSize:13, fontWeight:step===n?700:500, color:step===n?C.teal:unlocked?C.t3:C.t4, borderBottom:`2px solid ${step===n?C.teal:'transparent'}`, transition:'all 0.15s', opacity:unlocked?1:0.45 }}>
                {n}. {lbl}
              </button>
            );
          })}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ flex:1, display:'flex', flexDirection:'column', minHeight:0 }}>
          <div style={{ overflowY:'auto', padding:'20px 24px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px 14px', flex:1, minHeight:0, alignContent:'start' }}>
            {step === 1 && (<>
              <FieldWrap label="BSA Name" req err={errors.name} span>
                <input type="text" value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="Full name / firm name" style={errors.name?BSA_INPUT_ERR:BSA_INPUT} />
              </FieldWrap>
              <FieldWrap label="BSA Type" req err={errors.bsa_type}>
                <BsaSel field="bsa_type" opts={BSA_TYPES} err={errors.bsa_type} form={form} setForm={setForm}/>
              </FieldWrap>
              <FieldWrap label="Constitution" req err={errors.constitution}>
                <BsaSel field="constitution" opts={CONSTITUTIONS} err={errors.constitution} form={form} setForm={setForm}/>
              </FieldWrap>
              <FieldWrap label="Mobile Number" req err={errors.mobile_number}>
                <input type="text" value={form.mobile_number} onChange={e=>setForm(p=>({...p,mobile_number:e.target.value.replace(/[^0-9]/g,'')}))} placeholder="10-digit" maxLength={10} style={errors.mobile_number?BSA_INPUT_ERR:BSA_INPUT} />
              </FieldWrap>
              <FieldWrap label="Email" err={errors.email}>
                <input type="email" value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} placeholder="Optional" style={BSA_INPUT} />
              </FieldWrap>
              <FieldWrap label="PAN" req err={errors.pan}>
                <input type="text" value={form.pan} onChange={e=>setForm(p=>({...p,pan:e.target.value.toUpperCase()}))} placeholder="ABCDE1234F" maxLength={10} style={errors.pan?BSA_INPUT_ERR:BSA_INPUT} />
              </FieldWrap>
              <FieldWrap label="GST Number" err={errors.gst_number}>
                <input type="text" value={form.gst_number} onChange={e=>setForm(p=>({...p,gst_number:e.target.value.toUpperCase()}))} placeholder="Optional" style={BSA_INPUT} />
              </FieldWrap>
              <FieldWrap label="Payout Type" err={errors.payout_type}>
                <div style={{display:'flex',gap:16,marginTop:4}}>
                  <label style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:13,color:C.t1}}>
                    <input type="radio" name="payout_type" checked={form.payout_type==='disbursement'} onChange={()=>setForm(p=>({...p,payout_type:'disbursement',payout_percentage:''}))} style={{accentColor:C.navy,width:14,height:14}}/>Disbursement
                  </label>
                  <label style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:13,color:C.t1}}>
                    <input type="radio" name="payout_type" checked={form.payout_type==='company'} onChange={()=>setForm(p=>({...p,payout_type:'company',payout_percentage:''}))} style={{accentColor:C.navy,width:14,height:14}}/>Company
                  </label>
                </div>
              </FieldWrap>
              <FieldWrap label="Payout %" req err={errors.payout_percentage}>
                <input type="number" value={form.payout_percentage} onChange={e=>setForm(p=>({...p,payout_percentage:e.target.value}))} placeholder="e.g. 1.50" step="0.01" min="0" max="100" style={errors.payout_percentage?BSA_INPUT_ERR:BSA_INPUT} />
              </FieldWrap>
              <FieldWrap label="Address" req err={errors.address} span>
                <textarea value={form.address} onChange={e=>setForm(p=>({...p,address:e.target.value}))} placeholder="Full address" rows={2}
                  style={{ ...BSA_INPUT, height:'auto', paddingTop:10, paddingBottom:10, resize:'vertical', lineHeight:1.5 }} />
              </FieldWrap>
              <FieldWrap span>
                <div style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 12px', background:C.bg, borderRadius:8, border:`1px solid ${C.border}` }}>
                  <input type="checkbox" id="bsa_active" checked={!!form.is_active} onChange={e=>setForm(p=>({...p,is_active:e.target.checked}))} style={{ width:15, height:15, cursor:'pointer', accentColor:C.teal }}/>
                  <label htmlFor="bsa_active" style={{ fontSize:13, fontWeight:600, color:C.t2, cursor:'pointer' }}>Active</label>
                </div>
              </FieldWrap>
            </>)}
            {step === 2 && (<>
              <FieldWrap label="Bank Name" req err={errors.bank_name} span>
                <input type="text" value={form.bank_name} onChange={e=>setForm(p=>({...p,bank_name:e.target.value}))} placeholder="e.g. HDFC Bank" style={errors.bank_name?BSA_INPUT_ERR:BSA_INPUT} />
              </FieldWrap>
              <FieldWrap label="Account Holder Name" req err={errors.account_holder_name} span>
                <input type="text" value={form.account_holder_name} onChange={e=>setForm(p=>({...p,account_holder_name:e.target.value}))} placeholder="As per bank records" style={errors.account_holder_name?BSA_INPUT_ERR:BSA_INPUT} />
              </FieldWrap>
              <FieldWrap label="Account Number" req err={errors.account_number}>
                <input type="text" value={form.account_number} onChange={e=>setForm(p=>({...p,account_number:e.target.value}))} placeholder="" style={errors.account_number?BSA_INPUT_ERR:BSA_INPUT} />
              </FieldWrap>
              <FieldWrap label="IFSC Code" req err={errors.ifsc_code}>
                <input type="text" value={form.ifsc_code} onChange={e=>setForm(p=>({...p,ifsc_code:e.target.value.toUpperCase()}))} placeholder="e.g. HDFC0001234" style={errors.ifsc_code?BSA_INPUT_ERR:BSA_INPUT} />
              </FieldWrap>
              <div style={{ gridColumn:'1/-1', padding:'12px 14px', background:C.tealL, borderRadius:9, border:`1px solid ${C.tealB}`, fontSize:12.5, color:'#0F766E' }}>
                Bank details are used for payout processing. All fields are optional.
              </div>
            </>)}
            {step === 3 && (<>
              {/* ── Upload UI — works for both new and existing BSA ── */}
              <div style={{ gridColumn:'1/-1', display:'flex', flexDirection:'column', gap:12 }}>
                <div style={{ fontSize:12.5, fontWeight:700, color:C.t1 }}>
                  {isEdit ? 'Upload New Document' : 'Attach Documents (optional)'}
                </div>
                {!isEdit && (
                  <div style={{ fontSize:11.5, color:C.t3, marginTop:-6 }}>
                    Files will be uploaded automatically when you save the BSA.
                  </div>
                )}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px 12px' }}>
                  <div style={{ position:'relative' }}>
                    <select value={docType} onChange={e => setDocType(e.target.value)}
                      style={{ ...BSA_INPUT, cursor:'pointer', paddingRight:26, appearance:'none', WebkitAppearance:'none' }}>
                      {DOC_TYPES.map(dt => <option key={dt.value} value={dt.value}>{dt.label}</option>)}
                    </select>
                    <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', color:'#6B7280', fontSize:14, lineHeight:1 }}>▼</span>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
                      onChange={e => setDocFile(e.target.files[0] || null)}
                      style={{ flex:1, height:36, border:'1.5px solid #E5E7EB', borderRadius:8, padding:'0 10px', fontSize:12, color:C.t1, background:'#FAFBFC', boxSizing:'border-box', cursor:'pointer', fontFamily:'inherit' }}/>
                  </div>
                  <div style={{ gridColumn:'1/-1' }}>
                    {isEdit ? (
                      <button type="button" onClick={handleDocUpload} disabled={docUploading || !docFile}
                        style={{ height:36, padding:'0 18px', background:(docUploading||!docFile)?C.border:C.teal, color:(docUploading||!docFile)?C.t4:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:(docUploading||!docFile)?'not-allowed':'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:6 }}>
                        {docUploading ? <><Spin/> Uploading…</> : <><FiUpload size={13}/> Upload Document</>}
                      </button>
                    ) : (
                      <button type="button" disabled={!docFile}
                        onClick={() => {
                          if (!docFile) return;
                          setPendingDocs(prev => [...prev, { type: docType, file: docFile }]);
                          setDocFile(null);
                          if (fileRef.current) fileRef.current.value = '';
                        }}
                        style={{ height:36, padding:'0 18px', background:!docFile?C.border:C.teal, color:!docFile?C.t4:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:!docFile?'not-allowed':'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:6 }}>
                        <FiUpload size={13}/> Add to Queue
                      </button>
                    )}
                  </div>
                </div>
                {/* Pending docs queue (new BSA) */}
                {!isEdit && pendingDocs.length > 0 && (
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    <div style={{ fontSize:11.5, fontWeight:700, color:C.t2 }}>Queued ({pendingDocs.length})</div>
                    {pendingDocs.map((pd, i) => (
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', background:'#FAFBFC', border:`1px solid ${C.border}`, borderRadius:8 }}>
                        <FiFile size={13} style={{ color:C.teal, flexShrink:0 }}/>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:12, fontWeight:600, color:C.t1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{pd.file.name}</div>
                          <div style={{ fontSize:11, color:C.t4 }}>{DOC_TYPES.find(d=>d.value===pd.type)?.label || pd.type}</div>
                        </div>
                        <button type="button" onClick={() => setPendingDocs(prev => prev.filter((_,j) => j !== i))}
                          style={{ width:26, height:26, borderRadius:6, border:`1px solid ${C.redB}`, background:'#fff', color:C.red, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                          <FiX size={11}/>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* ── Uploaded docs list (edit mode) ── */}
              {isEdit && (
                <div style={{ gridColumn:'1/-1' }}>
                  <div style={{ fontSize:12, fontWeight:700, color:C.t2, marginBottom:10 }}>Uploaded Documents ({docs.length})</div>
                    {docsLoading ? (
                      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>{[1,2].map(i => <Sh key={i} h={44} r={8}/>)}</div>
                    ) : docs.length === 0 ? (
                      <div style={{ textAlign:'center', padding:'20px 16px', color:C.t4, fontSize:12.5 }}>No documents uploaded yet</div>
                    ) : (
                      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                        {docs.map(doc => {
                          const isDel = docDeleting === doc.id;
                          const fn = docFileName(doc.file || doc.document);
                          return (
                            <div key={doc.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', background:'#FAFBFC', border:'1px solid #E5E7EB', borderRadius:9, opacity:isDel?0.5:1, transition:'all 0.12s' }}>
                              <div style={{ width:32, height:32, borderRadius:8, background:C.tealL, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                <FiFile size={14} style={{ color:C.teal }}/>
                              </div>
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ fontSize:12.5, fontWeight:600, color:C.t1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{fn}</div>
                                <div style={{ fontSize:11, color:C.t4, marginTop:2 }}>{docLabel(doc.document_type)}</div>
                              </div>
                              {doc.file && (
                                <a href={doc.file} target="_blank" rel="noopener noreferrer"
                                  style={{ display:'flex', alignItems:'center', gap:4, fontSize:12, color:C.teal, fontWeight:600, padding:'4px 10px', background:C.tealL, borderRadius:7, textDecoration:'none', border:'1px solid #5EEAD4', flexShrink:0 }}>
                                  <FiDownload size={11}/> View
                                </a>
                              )}
                              <button type="button" onClick={() => handleDocDelete(doc.id)} disabled={isDel}
                                style={{ width:30, height:30, borderRadius:7, border:'1px solid #FECACA', background:'#fff', color:C.red, cursor:isDel?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, opacity:isDel?0.5:1 }}>
                                {isDel ? <div style={{ width:11, height:11, borderRadius:'50%', border:'2px solid #DC2626', borderTopColor:'transparent', animation:'cfSpin 0.8s linear infinite' }}/> : <FiTrash2 size={12}/>}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
              )}
            </>)}
          </div>

          {/* Footer */}
          <div style={{ padding:'14px 24px', borderTop:`1px solid ${C.borderL}`, display:'flex', gap:10, justifyContent:'space-between', background:'#FAFBFC', flexShrink:0, alignItems:'center' }}>
            <div style={{ display:'flex', gap:8 }}>
              {step > 1 && <button type="button" onClick={() => setStep(step - 1)} style={{ height:40, padding:'0 18px', border:`1px solid ${C.border}`, borderRadius:9, background:C.white, color:C.t2, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>← Back</button>}
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button type="button" onClick={onClose} disabled={saving} style={{ height:40, padding:'0 18px', border:`1px solid ${C.border}`, borderRadius:9, background:C.white, color:C.t2, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
              {step === 1 && <button type="button" onClick={() => goNext(2)} style={{ height:40, padding:'0 22px', background:C.teal, color:'#fff', border:'none', borderRadius:9, fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>Next → Bank Details</button>}
              {step === 2 && <button type="button" onClick={() => goNext(3)} style={{ height:40, padding:'0 22px', background:C.teal, color:'#fff', border:'none', borderRadius:9, fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>Next → Documents</button>}
              {step === 3 && (
                <button type="submit" disabled={saving} style={{ height:40, padding:'0 28px', minWidth:140, background:saving?'#0F766E':C.gold, color:saving?'#fff':'#0D1B2A', border:'none', borderRadius:9, fontSize:13, fontWeight:800, cursor:saving?'not-allowed':'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:7, boxShadow:'0 2px 10px rgba(245,166,35,0.38)' }}>
                  {saving ? <><Spin/> Saving...</> : <><FiSave size={14}/> {isEdit ? 'Update BSA' : 'Save BSA'}</>}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════════
   BSA TAB
═══════════════════════════════════════════════════════ */
function BsaTab({ openDrawerTrigger }) {
  const [bsaList,    setBsaList]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [page,       setPage]       = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editingBsa, setEditingBsa] = useState(null);
  const [deleting,   setDeleting]   = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [togglingBsa, setTogglingBsa] = useState(null);
  const BSA_PAGE = 15;

  useEffect(() => {
    if (openDrawerTrigger > 0) { setEditingBsa(null); setShowDrawer(true); }
  }, [openDrawerTrigger]);

  const fetchBsa = useCallback(async (pg = 1, q = '') => {
    setLoading(true);
    try {
      const params = { page: pg, page_size: BSA_PAGE };
      if (q.trim()) params.search = q.trim();
      const res  = await api.getBsaList(params);
      const data = res?.data;
      const arr  = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
      setBsaList(arr);
      setTotalCount(data?.count || arr.length);
      setPage(pg);
    } catch { toast.error('Failed to load BSA list'); }
    finally  { setLoading(false); }
  }, []);

  useEffect(() => { fetchBsa(1, ''); }, [fetchBsa]);

  const handleDelete = async (bsa) => {
    setDeleting(bsa.id); setConfirmDel(null);
    try {
      await api.deleteBsa(bsa.id);
      toast.success('BSA deleted');
      fetchBsa(page, search);
    } catch { toast.error('Failed to delete BSA'); }
    finally  { setDeleting(null); }
  };

  const handleToggleActive = async (bsa) => {
    setTogglingBsa(bsa.id);
    try {
      await api.patchBsa(bsa.id, { is_active: !bsa.is_active });
      toast.success(`BSA ${bsa.is_active ? 'deactivated' : 'activated'}`);
      fetchBsa(page, search);
    } catch { toast.error('Failed to update BSA status'); }
    finally  { setTogglingBsa(null); }
  };

  const bsaDrawerClose = useCallback(() => {
    setShowDrawer(false);
    setEditingBsa(null);
  }, []);

  const bsaDrawerSaved = useCallback(() => {
    setShowDrawer(false);
    setEditingBsa(null);
    fetchBsa(page, search);
  }, [page, search, fetchBsa]);

  const typeLabel = (t) => BSA_TYPES.find(b=>b.value===t)?.label || t || '—';

  return (
    <>
      <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, boxShadow:'0 2px 6px rgba(13,59,94,0.06)', overflow:'hidden' }}>
        {/* Toolbar */}
        <div style={{ padding:'11px 16px', borderBottom:`1px solid ${C.borderL}`, display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <FiBriefcase size={13} style={{ color:C.teal, opacity:0.8 }}/>
            <span style={{ fontSize:13, fontWeight:700, color:C.t1 }}>BSA Master</span>
            {!loading && <span style={{ background:C.tealL, color:C.teal, padding:'2px 8px', borderRadius:20, fontSize:10.5, fontWeight:700 }}>{totalCount}</span>}
          </div>
          <div style={{ position:'relative', flex:1, minWidth:180 }}>
            <FiSearch size={13} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:C.t4, pointerEvents:'none' }}/>
            <input type="text" placeholder="Search name, code, PAN..." value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key==='Enter') fetchBsa(1, search); }}
              style={{ ...inputSt(), height:36, paddingLeft:32 }} onFocus={onFocus} onBlur={onBlur}/>
          </div>
          <button onClick={() => fetchBsa(1, search)}
            style={{ height:36, padding:'0 14px', borderRadius:9, background:C.navy, border:'none', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:5 }}>
            <FiSearch size={12}/> Search
          </button>
          <button onClick={() => { setSearch(''); fetchBsa(1, ''); }} title="Reset"
            style={{ width:36, height:36, borderRadius:9, background:C.navyL, border:`1px solid ${C.border}`, color:C.navy, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <FiRefreshCw size={13}/>
          </button>
        </div>

        {/* Loading */}
        {loading && <div style={{ padding:16, display:'flex', flexDirection:'column', gap:10 }}>{[1,2,3,4,5].map(i=><Sh key={i} h={48} r={8}/>)}</div>}

        {/* Empty */}
        {!loading && bsaList.length === 0 && (
          <div style={{ padding:'48px 24px', textAlign:'center' }}>
            <div style={{ width:46, height:46, borderRadius:'50%', background:C.tealL, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px' }}>
              <FiBriefcase size={19} style={{ color:C.teal }}/>
            </div>
            <div style={{ fontSize:14, fontWeight:700, color:C.t1, marginBottom:5 }}>{search ? `No results for "${search}"` : 'No BSA records yet'}</div>
            <div style={{ fontSize:12.5, color:C.t4 }}>{search ? 'Try a different keyword' : 'Click "Add BSA" above to get started'}</div>
          </div>
        )}

        {/* Table */}
        {!loading && bsaList.length > 0 && (
          <>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', minWidth:640 }}>
                <thead>
                  <tr style={{ background:'#F7F9FB' }}>
                    {['#','Code','Name','Type','Mobile','Disb. Payout %','Company Payout %','Active'].map((col,i) => (
                      <th key={col} style={{ padding:'10px 14px', fontSize:10.5, fontWeight:800, color:C.t1, textTransform:'uppercase', letterSpacing:'0.08em', textAlign:'left', borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap', width:i===0?36:'auto' }}>{col}</th>
                    ))}
                    <th style={{ padding:'10px 14px', fontSize:10.5, fontWeight:800, color:C.t1, textTransform:'uppercase', letterSpacing:'0.08em', textAlign:'right', borderBottom:`1px solid ${C.border}`, width:84 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bsaList.map((bsa, idx) => {
                    const isLast = idx === bsaList.length - 1;
                    const isDel  = deleting === bsa.id;
                    const tc     = BSA_TYPE_COLORS[bsa.bsa_type] || { bg:'#F1F5F9', color:C.t2, border:C.border };
                    return (
                      <tr key={bsa.id} className="ms-tr" style={{ background:C.white, opacity:isDel?0.5:1, transition:'all 0.12s' }}>
                        <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t4 }}>{(page-1)*BSA_PAGE+idx+1}</td>
                        <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
                          <span style={{ fontFamily:'monospace', fontSize:11.5, background:C.navyL, color:C.navy, padding:'2px 8px', borderRadius:6 }}>{bsa.code||'—'}</span>
                        </td>
                        <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:13, fontWeight:600, color:C.t1 }}>{bsa.name}</td>
                        <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
                          {bsa.bsa_type
                            ? <span style={{ background:tc.bg, color:tc.color, border:`1px solid ${tc.border}`, padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700, whiteSpace:'nowrap' }}>{typeLabel(bsa.bsa_type)}</span>
                            : <span style={{ color:C.t4 }}>—</span>}
                        </td>
                        <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12.5, color:C.t3, fontFamily:'monospace' }}>{bsa.mobile_number||'—'}</td>
                        <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
                          {bsa.disbursement_payout_percentage != null && bsa.disbursement_payout_percentage !== ''
                            ? <span style={{ background:C.goldL, color:'#92400E', padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700 }}>{parseFloat(bsa.disbursement_payout_percentage).toFixed(2)}%</span>
                            : <span style={{ color:C.t4 }}>—</span>}
                        </td>
                        <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
                          {bsa.company_payout_percentage != null && bsa.company_payout_percentage !== ''
                            ? <span style={{ background:C.skyL, color:C.sky, padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700 }}>{parseFloat(bsa.company_payout_percentage).toFixed(2)}%</span>
                            : <span style={{ color:C.t4 }}>—</span>}
                        </td>
                        <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
                          {bsa.is_active !== false
                            ? <span style={{ background:C.greenL, color:C.green, border:`1px solid ${C.greenB}`, padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700 }}>Yes</span>
                            : <span style={{ background:C.redL, color:C.red, border:`1px solid ${C.redB}`, padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700 }}>No</span>}
                        </td>
                        <td style={{ padding:'11px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>
                          <div style={{ display:'flex', gap:6, justifyContent:'flex-end' }}>
                            <button className="ms-btn" title={bsa.is_active !== false ? 'Deactivate' : 'Activate'} disabled={isDel || togglingBsa === bsa.id}
                              onClick={() => handleToggleActive(bsa)}
                              style={{ color:bsa.is_active !== false ? C.red : C.green, borderColor:bsa.is_active !== false ? C.redB : C.greenB }}>
                              {togglingBsa === bsa.id
                                ? <div style={{ width:12, height:12, borderRadius:'50%', border:`2px solid ${bsa.is_active !== false ? C.red : C.green}`, borderTopColor:'transparent', animation:'cfSpin 0.8s linear infinite' }}/>
                                : bsa.is_active !== false ? <><FiX size={12}/></> : <><FiCheck size={12}/></>}
                            </button>
                            <button className="ms-btn" title="Edit" disabled={isDel} onClick={() => { setEditingBsa(bsa); setShowDrawer(true); }} style={{ color:C.navy, borderColor:C.border }}><FiEdit2 size={13}/></button>
                            <button className="ms-btn" title="Delete" disabled={isDel} onClick={() => setConfirmDel(bsa)} style={{ color:isDel?C.t4:C.red, borderColor:C.redB }}>
                              {isDel ? <div style={{ width:12, height:12, borderRadius:'50%', border:`2px solid ${C.red}`, borderTopColor:'transparent', animation:'cfSpin 0.8s linear infinite' }}/> : <FiTrash2 size={13}/>}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ borderTop:`1px solid ${C.borderL}` }}>
              <Pagination currentPage={page} totalCount={totalCount} pageSize={BSA_PAGE} onPageChange={p => fetchBsa(p, search)}/>
            </div>
          </>
        )}
      </div>

      {/* BSA Drawer */}
      {showDrawer && (
        <BsaDrawer
          editingBsa={editingBsa}
          onClose={bsaDrawerClose}
          onSaved={bsaDrawerSaved}
        />
      )}

      {/* Confirm Delete */}
      {confirmDel && (
        <div style={{ position:'fixed', inset:0, background:'rgba(13,27,42,0.55)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2100, padding:16 }}
          onClick={e=>{ if(e.target===e.currentTarget) setConfirmDel(null); }}>
          <div style={{ background:C.white, borderRadius:14, width:'100%', maxWidth:380, boxShadow:'0 24px 60px rgba(13,27,42,0.30)', overflow:'hidden', animation:'cfFadeUp 0.18s ease both' }}>
            <div style={{ padding:'15px 20px', borderBottom:`1px solid ${C.borderL}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontWeight:800, fontSize:14, color:C.t1 }}>Confirm Delete</span>
              <button onClick={()=>setConfirmDel(null)} style={{ background:'none', border:'none', cursor:'pointer', color:C.t4, display:'flex' }}><FiX size={16}/></button>
            </div>
            <div style={{ padding:'20px', textAlign:'center' }}>
              <div style={{ width:46, height:46, borderRadius:'50%', background:C.redL, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px' }}><FiTrash2 size={19} style={{ color:C.red }}/></div>
              <p style={{ fontSize:13.5, color:C.t2, margin:'0 0 4px', lineHeight:1.5 }}>Delete BSA <strong>"{confirmDel.name}"</strong>?</p>
              <p style={{ fontSize:12, color:C.t4, margin:0 }}>This action cannot be undone.</p>
            </div>
            <div style={{ padding:'12px 20px', borderTop:`1px solid ${C.borderL}`, display:'flex', gap:10, justifyContent:'flex-end', background:'#FAFBFC' }}>
              <button onClick={()=>setConfirmDel(null)} style={{ height:36, padding:'0 18px', border:`1px solid ${C.border}`, borderRadius:8, background:C.white, color:C.t2, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
              <button onClick={()=>handleDelete(confirmDel)} style={{ height:36, padding:'0 20px', background:C.red, color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:6 }}>
                <FiTrash2 size={13}/> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function Masters() {
  const [activeTab,   setActiveTab]   = useState('products');
  const [data,        setData]        = useState({ products:[], institutions:[], channels:[] });
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [showDrawer,  setShowDrawer]  = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form,        setForm]        = useState({});
  const [saving,      setSaving]      = useState(false);
  const [deleting,    setDeleting]    = useState(null);
  const [confirmDel,  setConfirmDel]  = useState(null);

  const [bankerCount,         setBankerCount]         = useState(0);
  const [bankerDrawerTrigger, setBankerDrawerTrigger] = useState(0);
  const [bsaCount,            setBsaCount]            = useState(0);
  const [bsaDrawerTrigger,    setBsaDrawerTrigger]    = useState(0);
  const [institutions,        setInstitutions]        = useState([]);

  const PAGE_SIZE = 15; // Match backend StandardPagination page_size
  const [pageMap,  setPageMap]  = useState({ products:1, institutions:1, channels:1 });
  const [countMap, setCountMap] = useState({ products:0, institutions:0, channels:0 });

  const [statuses,       setStatuses]       = useState([]);
  const [statusLoading,  setStatusLoading]  = useState(false);
  const [statusDrawer,   setStatusDrawer]   = useState(false);
  const [statusEdit,     setStatusEdit]     = useState(null);
  const [statusForm,     setStatusForm]     = useState({ name:'', parent:'' });
  const [statusSaving,   setStatusSaving]   = useState(false);
  const [statusDeleting, setStatusDeleting] = useState(null);
  const [statusConfirm,  setStatusConfirm]  = useState(null);

  // Import Excel state
  const [importing,     setImporting]     = useState(false);
  const [importResult,  setImportResult]  = useState(null); // {created, skipped, errors, message}
  const importFileRef = useRef(null);

  const fetchTab = useCallback(async (tabKey, pageNumber=1, silent=false) => {
    if (!silent) setLoading(true);
    try {
      const res   = await api[API_MAP[tabKey].get]({ page:pageNumber, page_size:PAGE_SIZE });
      const list  = res?.data?.results || [];
      const total = res?.data?.count   || 0;
      setData(prev => ({ ...prev, [tabKey]:list }));
      setCountMap(prev => ({ ...prev, [tabKey]:total }));
      setPageMap(prev => ({ ...prev, [tabKey]:pageNumber }));
    } catch { if (!silent) toast.error('Failed to load data'); }
    finally  { if (!silent) setLoading(false); }
  }, []);

  const fetchStatuses = useCallback(async () => {
    setStatusLoading(true);
    try {
      const res = await api.getLeadStatuses();
      setStatuses(res?.data?.results || []);
    } catch { toast.error('Failed to load statuses'); }
    finally  { setStatusLoading(false); }
  }, []);

  const fetchInstitutions = useCallback(async () => {
    try {
      const res = await api.getInstitutions({ page_size: 500 });
      const d   = res?.data;
      const arr = Array.isArray(d?.results) ? d.results : Array.isArray(d) ? d : [];
      setInstitutions(arr);
    } catch {}
  }, []);

  const fetchBankerCount = useCallback(async () => {
    try {
      const res = await api.getBankers({ page_size: 1 });
      setBankerCount(res?.data?.count || 0);
    } catch {}
  }, []);

  const fetchBsaCount = useCallback(async () => {
    try {
      const res = await api.getBsaList({ page_size: 1 });
      setBsaCount(res?.data?.count || 0);
    } catch {}
  }, []);

  useEffect(() => {
    Object.keys(API_MAP).forEach(tabKey => fetchTab(tabKey, 1, true));
    fetchStatuses();
    fetchInstitutions();
    fetchBankerCount();
    fetchBsaCount();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (activeTab === 'lead_status') fetchStatuses();
    else if (activeTab === 'bankers') { fetchInstitutions(); fetchBankerCount(); }
    else if (activeTab === 'bsa')    fetchBsaCount();
    else if (API_MAP[activeTab])     fetchTab(activeTab, pageMap[activeTab]);
  }, [activeTab]); // eslint-disable-line

  const openCreate  = () => { setEditingItem(null); setForm(emptyForm(activeTab)); setShowDrawer(true); };
  const openEdit    = (item) => { setEditingItem(item); setForm({ ...emptyForm(activeTab), ...item }); setShowDrawer(true); };
  const closeDrawer = () => { setShowDrawer(false); setEditingItem(null); };

  const handleSave = async (e) => {
    e.preventDefault();
    const missing = FIELD_CONFIG[activeTab].filter(f => f.required && !String(form[f.key]||'').trim());
    if (missing.length) { toast.error(`${missing[0].label} is required`); return; }
    try {
      setSaving(true);
      const payload = { ...form };
      const map = API_MAP[activeTab];
      if (editingItem) { await api[map.update](editingItem.id, payload); toast.success('Updated successfully'); }
      else             { await api[map.create](payload);                 toast.success('Created successfully'); }
      closeDrawer();
      fetchTab(activeTab, pageMap[activeTab]);
    } catch (err) {
      console.error(`Save ${activeTab} error:`, err);
      const data = err?.response?.data || err || {};
      if (typeof data === 'object' && Object.keys(data).length) {
        const msgs = Object.entries(data).map(([k,v]) => `${k}: ${Array.isArray(v)?v[0]:v}`).join(', ');
        toast.error(msgs);
      } else {
        toast.error(err?.message || 'Failed to save');
      }
    }
    finally  { setSaving(false); }
  };

  const handleDelete = async (item) => {
    try {
      setDeleting(item.id); setConfirmDel(null);
      await api[API_MAP[activeTab].del](item.id);
      toast.success('Deleted successfully');
      fetchTab(activeTab, pageMap[activeTab]);
    } catch { toast.error('Failed to delete'); }
    finally  { setDeleting(null); }
  };

  const openStatusCreate  = () => { setStatusEdit(null); setStatusForm({ name:'', parent:'' }); setStatusDrawer(true); };
  const openStatusEdit    = (item) => { setStatusEdit(item); setStatusForm({ name:item.name||'', parent:item.parent?.id?String(item.parent.id):'' }); setStatusDrawer(true); };
  const closeStatusDrawer = () => { setStatusDrawer(false); setStatusEdit(null); };

  const handleStatusSave = async (e) => {
    e.preventDefault();
    if (!statusForm.name.trim()) { toast.error('Status name is required'); return; }
    try {
      setStatusSaving(true);
      const user = JSON.parse(localStorage.getItem('user')||'{}');
      const payload = { name:statusForm.name.trim(), parent:statusForm.parent?parseInt(statusForm.parent):null, created_by:user?.id||null, updated_by:user?.id||null };
      if (statusEdit) { await api.updateLeadStatus(statusEdit.id, payload); toast.success('Status updated'); }
      else            { await api.createLeadStatus(payload);                toast.success('Status created'); }
      closeStatusDrawer(); fetchStatuses();
    } catch (err) { toast.error(err?.message||'Failed to save status'); }
    finally       { setStatusSaving(false); }
  };

  const handleStatusDelete = async (item) => {
    const childCount = statuses.filter(s => s.parent?.id===item.id).length;
    if (childCount>0) { toast.error(`Cannot delete — "${item.name}" has ${childCount} sub-status(es). Delete them first.`); return; }
    try {
      setStatusDeleting(item.id); setStatusConfirm(null);
      await api.deleteLeadStatus(item.id);
      toast.success('Status deleted'); fetchStatuses();
    } catch { toast.error('Failed to delete status'); }
    finally  { setStatusDeleting(null); }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset file input so same file can be re-selected
    e.target.value = '';
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.importInstitutions(fd);
      const result = res?.data;
      setImportResult(result);
      if (result?.created_count > 0) {
        toast.success(`${result.created_count} institution(s) imported!`);
        fetchTab('institutions', 1);
      } else if (result?.skipped_count > 0 && result?.created_count === 0) {
        toast.info('No new records — all rows already exist (duplicates).');
      } else {
        toast.warning(result?.message || 'Import completed with issues.');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Import failed';
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  const rows = (data[activeTab]||[]).filter(item =>
    !search || Object.values(item).some(v => String(v).toLowerCase().includes(search.toLowerCase()))
  );
  const tab = TABS.find(t => t.id===activeTab);
  const parentStatuses = statuses.filter(s => !s.parent);
  const childStatuses  = statuses.filter(s =>  s.parent);
  const statusTree = parentStatuses.map((p,idx) => ({
    parent:{ ...p, _colorIdx:idx%PARENT_COLORS.length },
    children:childStatuses.filter(c => c.parent?.id===p.id),
  }));
  const filteredTree = statusTree.map(node => ({
    ...node,
    children:node.children.filter(c => !search||c.name.toLowerCase().includes(search.toLowerCase())||(c.code||'').toLowerCase().includes(search.toLowerCase())),
  })).filter(node => !search||node.parent.name.toLowerCase().includes(search.toLowerCase())||(node.parent.code||'').toLowerCase().includes(search.toLowerCase())||node.children.length>0);

  const COLUMNS = {
    products:     ['#','Name','Active'],
    institutions: ['#','Name','Code','Type','Payout %','Active'],
    channels:     ['#','Name','Contact','Email','GSTIN','Payout %'],
  };

  const rowCells = (item) => {
    switch (activeTab) {
      case 'products': return [
        item.name,
        item.is_active
          ? <span style={{ background:C.greenL, color:C.green, border:`1px solid ${C.greenB}`, padding:'2px 9px', borderRadius:20, fontSize:11, fontWeight:700 }}>Yes</span>
          : <span style={{ background:C.redL, color:C.red, border:`1px solid ${C.redB}`, padding:'2px 9px', borderRadius:20, fontSize:11, fontWeight:700 }}>No</span>,
      ];
      case 'institutions': {
        const tc = TYPE_COLORS[item.institution_type]||{ bg:'#F1F5F9', color:C.t2 };
        return [
          item.name,
          <span style={{ fontFamily:'monospace', fontSize:12, color:C.t2 }}>{item.code||'—'}</span>,
          item.institution_type
            ? <span style={{ background:tc.bg, color:tc.color, padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700, whiteSpace:'nowrap' }}>{TYPE_LABELS[item.institution_type]||item.institution_type}</span>
            : <span style={{ color:C.t4 }}>—</span>,
          item.payout_rate != null && item.payout_rate !== '' && parseFloat(item.payout_rate) !== 0
            ? <span style={{ background:C.goldL, color:'#92400E', padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700 }}>{parseFloat(item.payout_rate).toFixed(2)}%</span>
            : <span style={{ color:C.t4 }}>—</span>,
          item.is_active
            ? <span style={{ background:C.greenL, color:C.green, border:`1px solid ${C.greenB}`, padding:'2px 9px', borderRadius:20, fontSize:11, fontWeight:700 }}>Yes</span>
            : <span style={{ background:C.redL, color:C.red, border:`1px solid ${C.redB}`, padding:'2px 9px', borderRadius:20, fontSize:11, fontWeight:700 }}>No</span>,
        ];
      }
      case 'channels': return [
        item.name,
        item.contact_person||<span style={{ color:C.t4 }}>—</span>,
        item.email||<span style={{ color:C.t4 }}>—</span>,
        item.gstin||<span style={{ color:C.t4 }}>—</span>,
        item.payout_percent!=null&&item.payout_percent!==''
          ? <span style={{ background:C.goldL, color:'#92400E', padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700 }}>{item.payout_percent}%</span>
          : <span style={{ color:C.t4 }}>—</span>,
      ];
      default: return [];
    }
  };

  const renderField = (f) => {
    if (f.type==='select') return (
      <div style={{ position:'relative' }}>
        <select value={form[f.key]||''} required={f.required} onChange={e=>setForm(p=>({...p,[f.key]:e.target.value}))}
          style={{ ...inputSt(), cursor:'pointer', paddingRight:28 }} onFocus={onFocus} onBlur={onBlur}>
          <option value="">Select {f.label}</option>
          {f.options.map(o => { const v=typeof o==='object'?o.value:o; const l=typeof o==='object'?o.label:o; return <option key={v} value={v}>{l}</option>; })}
        </select>
        <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', color:C.t4, fontSize:10 }}>&#9662;</span>
      </div>
    );
    if (f.type==='checkbox') return (
      <div style={{ display:'flex', alignItems:'center', gap:8, paddingTop:8 }}>
        <input type="checkbox" checked={!!form[f.key]} onChange={e=>setForm(p=>({...p,[f.key]:e.target.checked}))} style={{ width:16, height:16, cursor:'pointer', accentColor:C.navy }}/>
        <span style={{ fontSize:13, color:C.t2 }}>Yes, {f.label.toLowerCase()}</span>
      </div>
    );
    return (
      <input type={f.type||'text'} value={form[f.key]||''} onChange={e=>setForm(p=>({...p,[f.key]:e.target.value}))}
        required={f.required} step={f.type==='number'?'0.01':undefined} min={f.type==='number'?'0':undefined}
        placeholder={`Enter ${f.label.toLowerCase()}`} style={inputSt()} onFocus={onFocus} onBlur={onBlur}/>
    );
  };

  return (
    <>
      <style>{`
        @keyframes cfShimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        @keyframes cfSpin    { from{transform:rotate(0)} to{transform:rotate(360deg)} }
        @keyframes cfFadeUp  { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes cfSlideIn { from{transform:translateX(100%)} to{transform:translateX(0)} }
        .ms-tr:hover    { background:#F7F9FC !important; }
        .ms-btn         { width:30px;height:30px;border-radius:8px;border:1.5px solid #E5E7EB;background:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all 0.13s; }
        .ms-btn:hover   { transform:translateY(-1px);box-shadow:0 3px 8px rgba(0,0,0,0.10); }
        .ms-btn:disabled{ opacity:0.4;cursor:not-allowed;transform:none; }
        .ms-parent:hover{ filter:brightness(0.97); }
        .ms-child:hover { background:#F7F9FC !important; }
        /* ── Responsive ── */
        .ms-drawer      { width:500px !important; }
        .ms-drawer-grid { grid-template-columns:1fr 1fr; }
        .ms-hero-title  { font-size:22px !important; }
        .ms-hero-pad    { padding:20px 28px !important; }
        .ms-hero-sub    { display:block !important; }
        @media (max-width:640px) {
          .ms-drawer      { width:100vw !important; }
          .ms-drawer-grid { grid-template-columns:1fr !important; }
          .ms-hero-title  { font-size:17px !important; }
          .ms-hero-pad    { padding:14px 16px !important; }
          .ms-hero-sub    { display:none !important; }
          .ms-bk-table    { display:none !important; }
          .ms-bk-cards    { display:flex !important; }
          .ms-tab-table   { display:none !important; }
          .ms-tab-cards   { display:flex !important; }
        }
        @media (max-width:768px) {
          .ms-drawer      { width:90vw !important; }
          .ms-drawer-grid { grid-template-columns:1fr !important; }
          .ms-hero-title  { font-size:19px !important; }
          .ms-hero-pad    { padding:16px 20px !important; }
        }
      `}</style>

      <Layout pageTitle="Master Data">
        <div style={{ display:'flex', flexDirection:'column', gap:14, fontFamily:"'Plus Jakarta Sans',sans-serif", animation:'cfFadeUp 0.22s ease both' }}>

          {/* HERO */}
          <div className="ms-hero-pad" style={{ borderRadius:14, padding:'20px 28px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:14, background:'linear-gradient(120deg,#0D3B5E,#1A5276)', boxShadow:'0 4px 20px rgba(13,59,94,0.20)', position:'relative', overflow:'hidden' }}>
            <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:'linear-gradient(90deg,#F5A623,#FBBF4A,transparent)' }}/>
            <div>
              <div style={{ fontSize:11, fontWeight:600, color:'rgba(255,255,255,0.55)', marginBottom:4, letterSpacing:'0.06em', textTransform:'uppercase' }}>Configuration</div>
              <h2 className="ms-hero-title" style={{ margin:0, color:'#fff', fontSize:22, fontWeight:800, letterSpacing:'-0.03em', lineHeight:1.2 }}>Master Data</h2>
              <div className="ms-hero-sub" style={{ fontSize:12.5, color:'rgba(255,255,255,0.55)', marginTop:4 }}>Manage dropdown values used across leads, conversions and closures</div>
            </div>
            {activeTab !== 'bankers' && activeTab !== 'lead_status' && activeTab !== 'bsa' && (
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                {/* Import Excel — only for Institutions tab */}
                {activeTab === 'institutions' && (
                  <>
                    <input
                      ref={importFileRef}
                      type="file"
                      accept=".xlsx,.xls"
                      style={{ display:'none' }}
                      onChange={handleImportExcel}
                    />
                    <button
                      onClick={() => importFileRef.current?.click()}
                      disabled={importing}
                      style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'9px 16px', borderRadius:9, background:'#0D9488', color:'#fff', border:'none', fontWeight:700, fontSize:13, cursor:importing?'not-allowed':'pointer', fontFamily:'inherit', opacity:importing?0.7:1, transition:'all 0.12s' }}>
                      {importing
                        ? <><div style={{ width:13, height:13, borderRadius:'50%', border:'2px solid rgba(255,255,255,0.35)', borderTopColor:'#fff', animation:'cfSpin 0.7s linear infinite' }}/> Importing…</>
                        : <><FiUpload size={14}/> Import Excel</>
                      }
                    </button>
                  </>
                )}
                <button onClick={openCreate}
                  style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'9px 20px', borderRadius:9, background:C.gold, color:'#0D1B2A', border:'none', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit', boxShadow:'0 2px 10px rgba(245,166,35,0.45)', transition:'all 0.12s' }}
                  onMouseEnter={e=>{e.currentTarget.style.background='#FBBF4A';e.currentTarget.style.transform='translateY(-1px)';}}
                  onMouseLeave={e=>{e.currentTarget.style.background=C.gold;e.currentTarget.style.transform='';}}><FiPlus size={15}/> Add {tab?.label}</button>
              </div>
            )}
            {activeTab === 'bsa' && (
              <button onClick={() => setBsaDrawerTrigger(t => t + 1)}
                style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'9px 20px', borderRadius:9, background:C.gold, color:'#0D1B2A', border:'none', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit', boxShadow:'0 2px 10px rgba(245,166,35,0.45)', transition:'all 0.12s' }}
                onMouseEnter={e=>{e.currentTarget.style.background='#FBBF4A';e.currentTarget.style.transform='translateY(-1px)';}}
                onMouseLeave={e=>{e.currentTarget.style.background=C.gold;e.currentTarget.style.transform='';}}><FiPlus size={15}/> Add BSA</button>
            )}
            {activeTab === 'bankers' && (
              <button onClick={() => setBankerDrawerTrigger(t => t + 1)}
                style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'9px 20px', borderRadius:9, background:C.gold, color:'#0D1B2A', border:'none', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit', boxShadow:'0 2px 10px rgba(245,166,35,0.45)', transition:'all 0.12s' }}
                onMouseEnter={e=>{e.currentTarget.style.background='#FBBF4A';e.currentTarget.style.transform='translateY(-1px)';}}
                onMouseLeave={e=>{e.currentTarget.style.background=C.gold;e.currentTarget.style.transform='';}}><FiPlus size={15}/> Add Banker</button>
            )}
            {activeTab === 'lead_status' && (
              <button onClick={openStatusCreate}
                style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'9px 20px', borderRadius:9, background:C.gold, color:'#0D1B2A', border:'none', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit', boxShadow:'0 2px 10px rgba(245,166,35,0.45)', transition:'all 0.12s' }}
                onMouseEnter={e=>{e.currentTarget.style.background='#FBBF4A';e.currentTarget.style.transform='translateY(-1px)';}}
                onMouseLeave={e=>{e.currentTarget.style.background=C.gold;e.currentTarget.style.transform='';}}><FiPlus size={15}/> Add Lead Status</button>
            )}
          </div>

          {/* TABS */}
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', overflowX:'auto', WebkitOverflowScrolling:'touch', paddingBottom:2 }}>
            {TABS.map(t => {
              const isAct = activeTab===t.id;
              const count = t.id==='lead_status' ? statuses.length : t.id==='bankers' ? bankerCount : t.id==='bsa' ? bsaCount : (countMap[t.id] || (data[t.id]||[]).length);
              return (
                <button key={t.id} onClick={() => { setActiveTab(t.id); setSearch(''); setBankerDrawerTrigger(0); setBsaDrawerTrigger(0); }}
                  style={{ display:'flex', alignItems:'center', gap:7, padding:'9px 18px', border:'none', borderRadius:10, cursor:'pointer', fontFamily:'inherit', fontSize:13, transition:'all 0.15s', background:isAct?t.activeBg:t.bg, color:isAct?'#fff':t.color, fontWeight:isAct?700:600, boxShadow:isAct?`0 3px 10px ${t.shadow}`:'none', transform:isAct?'translateY(-1px)':'none' }}
                  onMouseEnter={e=>{ if(!isAct){ e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.boxShadow=`0 3px 10px ${t.shadow}`; } }}
                  onMouseLeave={e=>{ if(!isAct){ e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='none'; } }}>
                  {t.icon} {t.label}
                  <span style={{ background:isAct?'rgba(255,255,255,0.25)':t.color+'20', color:isAct?'#fff':t.color, borderRadius:20, padding:'1px 8px', fontSize:10.5, fontWeight:700 }}>{count}</span>
                </button>
              );
            })}
          </div>

          {/* BANKERS TAB */}
          {activeTab === 'bankers' && (
            <BankersTab institutions={institutions} openDrawerTrigger={bankerDrawerTrigger}/>
          )}

          {/* BSA TAB */}
          {activeTab === 'bsa' && (
            <BsaTab openDrawerTrigger={bsaDrawerTrigger}/>
          )}

          {/* LEAD STATUS TAB */}
          {activeTab === 'lead_status' && (
            statusLoading ? (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>{[1,2,3].map(i=><Sh key={i} h={72} r={12}/>)}</div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                  <div style={{ position:'relative', flex:1, maxWidth:340 }}>
                    <FiSearch size={13} style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', color:C.t4, pointerEvents:'none' }}/>
                    <input type="text" placeholder="Search statuses..." value={search} onChange={e=>setSearch(e.target.value)}
                      style={{ ...inputSt(), height:38, paddingLeft:34 }} onFocus={onFocus} onBlur={onBlur}/>
                  </div>
                  <button onClick={fetchStatuses} title="Refresh" style={{ width:38, height:38, borderRadius:9, background:C.navyL, border:`1px solid ${C.border}`, color:C.navy, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <FiRefreshCw size={14}/>
                  </button>
                </div>
                <div style={{ background:C.goldL, border:'1px solid #FDE68A', borderRadius:10, padding:'10px 14px', display:'flex', alignItems:'flex-start', gap:8, fontSize:12.5, color:'#92400E' }}>
                  <FiLayers size={14} style={{ flexShrink:0, marginTop:1 }}/>
                  <span><strong>Parent statuses</strong> are top-level (e.g. Follow Ups, Converted). <strong>Sub-statuses</strong> belong to a parent (e.g. Login under Converted).</span>
                </div>
                {statuses.length===0 ? (
                  <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:'52px 24px', textAlign:'center' }}>
                    <div style={{ fontSize:14, fontWeight:700, color:C.t1, marginBottom:6 }}>No statuses yet</div>
                    <div style={{ fontSize:12.5, color:C.t4 }}>Click "Add Lead Status" to create one</div>
                  </div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    {filteredTree.map(({ parent:p, children }) => {
                      const pc = PARENT_COLORS[p._colorIdx];
                      return (
                        <div key={p.id} style={{ border:`1px solid ${pc.border}`, borderRadius:12, overflow:'hidden', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
                          <div className="ms-parent" style={{ background:pc.bg, padding:'12px 16px', display:'flex', alignItems:'center', gap:12 }}>
                            <div style={{ width:36, height:36, borderRadius:9, background:pc.color, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><FiLayers size={15}/></div>
                            <div style={{ flex:1 }}>
                              <div style={{ fontWeight:700, fontSize:13.5, color:pc.color }}>{p.name}</div>
                              <div style={{ fontSize:11, color:pc.color, opacity:0.7, fontFamily:'monospace', marginTop:1 }}>code: {p.code}</div>
                            </div>
                            <span style={{ background:pc.color, color:'#fff', borderRadius:20, padding:'2px 10px', fontSize:11, fontWeight:700, whiteSpace:'nowrap' }}>Parent &middot; {children.length} sub-status{children.length!==1?'es':''}</span>
                            <div style={{ display:'flex', gap:8 }}>
                              <button className="ms-btn" title="Edit" onClick={() => openStatusEdit(p)} style={{ color:pc.color, borderColor:pc.border }}><FiEdit2 size={13}/></button>
                              <button className="ms-btn" onClick={() => children.length>0?toast.error('Delete sub-statuses first'):setStatusConfirm(p)} disabled={statusDeleting===p.id||children.length>0} style={{ color:children.length>0?C.border:C.red, borderColor:children.length>0?C.borderL:C.redB }}>
                                {statusDeleting===p.id ? <div style={{ width:12, height:12, borderRadius:'50%', border:`2px solid ${C.red}`, borderTopColor:'transparent', animation:'cfSpin 0.8s linear infinite' }}/> : <FiTrash2 size={13}/>}
                              </button>
                            </div>
                          </div>
                          {children.map((child, ci) => (
                            <div key={child.id} className="ms-child" style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 16px 10px 20px', borderTop:ci===0?`1px solid ${pc.border}`:`1px solid ${C.border}`, background:C.white, transition:'background 0.12s' }}>
                              <div style={{ display:'flex', alignItems:'center', gap:4, color:C.t4, flexShrink:0 }}><div style={{ width:20, height:1, background:C.border }}/><FiChevronRight size={12}/></div>
                              <div style={{ flex:1 }}>
                                <span style={{ fontWeight:600, fontSize:13, color:C.t1 }}>{child.name}</span>
                                <span style={{ marginLeft:8, fontSize:11, fontFamily:'monospace', background:C.bg, color:C.t2, padding:'1px 6px', borderRadius:5 }}>{child.code}</span>
                              </div>
                              <div style={{ display:'flex', gap:8 }}>
                                <button className="ms-btn" title="Edit" onClick={() => openStatusEdit(child)} style={{ color:C.navy, borderColor:C.border }}><FiEdit2 size={13}/></button>
                                <button className="ms-btn" onClick={() => setStatusConfirm(child)} disabled={statusDeleting===child.id} style={{ color:statusDeleting===child.id?C.t4:C.red, borderColor:C.redB }}>
                                  {statusDeleting===child.id ? <div style={{ width:12, height:12, borderRadius:'50%', border:`2px solid ${C.red}`, borderTopColor:'transparent', animation:'cfSpin 0.8s linear infinite' }}/> : <FiTrash2 size={13}/>}
                                </button>
                              </div>
                            </div>
                          ))}
                          {children.length===0 && <div style={{ padding:'8px 16px 8px 20px', borderTop:`1px solid ${pc.border}`, background:C.white, fontSize:12, color:C.t4, fontStyle:'italic' }}>No sub-statuses yet.</div>}
                        </div>
                      );
                    })}
                    {(() => {
                      const orphans = childStatuses.filter(c => !parentStatuses.find(p => p.id===c.parent?.id));
                      if (!orphans.length) return null;
                      return (
                        <div style={{ border:`1px solid ${C.redB}`, borderRadius:12, overflow:'hidden' }}>
                          <div style={{ background:C.redL, padding:'10px 16px', fontWeight:700, color:C.red, fontSize:13, display:'flex', alignItems:'center', gap:6 }}><FiAlertCircle size={14}/> Unassigned sub-statuses</div>
                          {orphans.map(o => (
                            <div key={o.id} style={{ padding:'10px 16px', borderTop:`1px solid ${C.redB}`, display:'flex', alignItems:'center', gap:12, background:C.white }}>
                              <span style={{ flex:1, fontSize:13, color:C.t1, fontWeight:600 }}>{o.name}</span>
                              <span style={{ fontSize:11, fontFamily:'monospace', color:C.t3 }}>{o.code}</span>
                              <button className="ms-btn" onClick={() => openStatusEdit(o)} style={{ color:C.navy }}><FiEdit2 size={13}/></button>
                              <button className="ms-btn" style={{ color:C.red }} onClick={() => handleStatusDelete(o)}><FiTrash2 size={13}/></button>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )
          )}

          {/* PRODUCTS / INSTITUTIONS / CHANNELS */}
          {!['bankers','lead_status','bsa'].includes(activeTab) && (
            <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, boxShadow:'0 2px 6px rgba(13,59,94,0.06)', overflow:'hidden' }}>
              <div style={{ padding:'12px 16px', borderBottom:`1px solid ${C.borderL}`, display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ color:tab?.color, display:'flex', opacity:0.8 }}>{tab?.icon}</span>
                  <span style={{ fontSize:13, fontWeight:700, color:C.t1 }}>{tab?.label}</span>
                  {!loading && <span style={{ background:C.navyL, color:C.navy, padding:'2px 8px', borderRadius:20, fontSize:10.5, fontWeight:700 }}>{countMap[activeTab]||rows.length}</span>}
                </div>
                <div style={{ position:'relative', width:280, marginLeft:'auto' }}>
                  <FiSearch size={13} style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', color:C.t4, pointerEvents:'none' }}/>
                  <input type="text" placeholder={`Search ${tab?.label}...`} value={search} onChange={e=>setSearch(e.target.value)}
                    style={{ ...inputSt(), height:36, paddingLeft:34 }} onFocus={onFocus} onBlur={onBlur}/>
                </div>
              </div>
              {/* Import Result Panel */}
              {importResult && activeTab === 'institutions' && (
                <div style={{ margin:'0 0 12px', padding:'12px 16px', borderRadius:10, background: importResult.error_count > 0 ? '#FEF2F2' : importResult.created_count > 0 ? '#DCFCE7' : '#FEF3DC', border:`1px solid ${importResult.error_count > 0 ? '#FECACA' : importResult.created_count > 0 ? '#86EFAC' : '#FDE68A'}`, display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12 }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color: importResult.error_count > 0 ? '#991B1B' : importResult.created_count > 0 ? '#166534' : '#92400E', marginBottom:4 }}>
                      Import Result: {importResult.message}
                    </div>
                    <div style={{ fontSize:12, color:'#374151', display:'flex', gap:16, flexWrap:'wrap' }}>
                      <span style={{ color:'#166534', fontWeight:600 }}>✓ {importResult.created_count} Created</span>
                      <span style={{ color:'#92400E', fontWeight:600 }}>⟳ {importResult.skipped_count} Skipped (duplicates)</span>
                      {importResult.error_count > 0 && <span style={{ color:'#991B1B', fontWeight:600 }}>✗ {importResult.error_count} Errors</span>}
                    </div>
                    {importResult.errors?.length > 0 && (
                      <div style={{ marginTop:6, fontSize:11.5, color:'#991B1B' }}>
                        {importResult.errors.slice(0,3).map((e,i) => <div key={i}>Row {e.row}: {e.reason}</div>)}
                        {importResult.errors.length > 3 && <div>…and {importResult.errors.length - 3} more errors</div>}
                      </div>
                    )}
                  </div>
                  <button onClick={() => setImportResult(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'#6B7280', fontSize:16, padding:0, lineHeight:1, flexShrink:0 }}>✕</button>
                </div>
              )}
              {loading && <div style={{ padding:16, display:'flex', flexDirection:'column', gap:10 }}>{[1,2,3,4,5].map(i=><Sh key={i} h={48} r={8}/>)}</div>}
              {!loading && rows.length===0 && (
                <div style={{ padding:'52px 24px', textAlign:'center' }}>
                  <div style={{ fontSize:14, fontWeight:700, color:C.t1, marginBottom:6 }}>{search?`No results for "${search}"` : `No ${tab?.label} yet`}</div>
                  <div style={{ fontSize:12.5, color:C.t3 }}>{search?'Try a different keyword':`Click "Add ${tab?.label}" to create one`}</div>
                </div>
              )}
              {!loading && rows.length>0 && (
                <>
                  {/* Desktop table */}
                  <div className="ms-tab-table">
                    <div style={{ overflowX:'auto' }}>
                      <table style={{ width:'100%', borderCollapse:'collapse', minWidth:500 }}>
                        <thead>
                          <tr style={{ background:'#F7F9FB' }}>
                            {COLUMNS[activeTab].map((col,i) => (
                              <th key={col} style={{ padding:'10px 16px', fontSize:10.5, fontWeight:800, color:C.t1, textTransform:'uppercase', letterSpacing:'0.08em', textAlign:'left', borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap', width:i===0?40:'auto' }}>{col}</th>
                            ))}
                            <th style={{ padding:'10px 16px', fontSize:10.5, fontWeight:800, color:C.t1, textTransform:'uppercase', letterSpacing:'0.08em', textAlign:'right', borderBottom:`1px solid ${C.border}`, width:90 }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((item,idx) => {
                            const isLast = idx===rows.length-1;
                            const isDel  = deleting===item.id;
                            return (
                              <tr key={item.id} className="ms-tr" style={{ background:C.white, opacity:isDel?0.5:1, transition:'all 0.12s' }}>
                                <td style={{ padding:'12px 16px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t4 }}>{idx+1}</td>
                                {rowCells(item).map((cell,ci) => (
                                  <td key={ci} style={{ padding:'12px 16px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:13, color:C.t1, fontWeight:ci===0?600:400 }}>{cell}</td>
                                ))}
                                <td style={{ padding:'12px 16px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>
                                  <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                                    <button className="ms-btn" title="Edit" onClick={() => openEdit(item)} disabled={isDel} style={{ color:C.navy, borderColor:C.border }}><FiEdit2 size={13}/></button>
                                    <button className="ms-btn" title="Delete" onClick={() => setConfirmDel(item)} disabled={isDel} style={{ color:isDel?C.t4:C.red, borderColor:C.redB }}>
                                      {isDel ? <div style={{ width:12, height:12, borderRadius:'50%', border:`2px solid ${C.red}`, borderTopColor:'transparent', animation:'cfSpin 0.8s linear infinite' }}/> : <FiTrash2 size={13}/>}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {/* Mobile cards */}
                  <div className="ms-tab-cards" style={{ display:'none', padding:'10px', flexDirection:'column', gap:8 }}>
                    {rows.map((item, idx) => {
                      const isDel = deleting===item.id;
                      const cells = rowCells(item);
                      const cols  = COLUMNS[activeTab].slice(1); // skip '#'
                      return (
                        <div key={item.id} style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:10, padding:'12px 14px', opacity:isDel?0.5:1, transition:'all 0.12s' }}>
                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                            <div style={{ fontSize:13.5, fontWeight:700, color:C.t1 }}>{item.name || cells[0]}</div>
                            <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                              <button className="ms-btn" title="Edit" onClick={() => openEdit(item)} disabled={isDel} style={{ color:C.navy, borderColor:C.border }}><FiEdit2 size={14}/></button>
                              <button className="ms-btn" title="Delete" onClick={() => setConfirmDel(item)} disabled={isDel} style={{ color:isDel?C.t4:C.red, borderColor:C.redB }}>
                                {isDel ? <div style={{ width:12, height:12, borderRadius:'50%', border:`2px solid ${C.red}`, borderTopColor:'transparent', animation:'cfSpin 0.8s linear infinite' }}/> : <FiTrash2 size={14}/>}
                              </button>
                            </div>
                          </div>
                          <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                            {cols.slice(1).map((col, ci) => (
                              <div key={col} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12 }}>
                                <span style={{ color:C.t4, minWidth:72 }}>{col}</span>
                                <span style={{ color:C.t2 }}>{cells[ci+1]}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ borderTop:`1px solid ${C.borderL}` }}>
                    <Pagination currentPage={pageMap[activeTab]} totalCount={countMap[activeTab]} pageSize={PAGE_SIZE} onPageChange={p => fetchTab(activeTab, p)}/>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </Layout>

      {/* PRODUCTS / INSTITUTIONS / CHANNELS DRAWER */}
      {showDrawer && !['bankers','lead_status','bsa'].includes(activeTab) && (
        <div style={{ position:'fixed', inset:0, zIndex:2000, display:'flex' }}>
          <div style={{ flex:1, background:'rgba(13,27,42,0.45)', backdropFilter:'blur(3px)' }} onClick={closeDrawer}/>
          <div className="ms-drawer" style={{ width:500, background:C.white, boxShadow:'-8px 0 40px rgba(13,27,42,0.20)', display:'flex', flexDirection:'column', animation:'cfSlideIn 0.24s ease both' }}>
            <div style={{ padding:'18px 24px', background:'linear-gradient(120deg,#0D3B5E,#1A5276)', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
              <div>
                <div style={{ fontSize:10.5, fontWeight:700, color:'rgba(255,255,255,0.50)', marginBottom:3, textTransform:'uppercase', letterSpacing:'0.08em' }}>{tab?.label}</div>
                <div style={{ fontSize:17, fontWeight:800, color:'#fff', letterSpacing:'-0.02em' }}>
                  {editingItem?'Edit':'Add'} {tab?.label}
                  {editingItem && <span style={{ fontSize:11, fontWeight:400, opacity:0.55, marginLeft:7 }}>#{editingItem.id}</span>}
                </div>
              </div>
              <button onClick={closeDrawer} style={{ width:36, height:36, borderRadius:9, background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.20)', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}><FiX size={17}/></button>
            </div>
            <form onSubmit={handleSave} style={{ flex:1, display:'flex', flexDirection:'column', minHeight:0 }}>
              <div className="ms-drawer-grid" style={{ overflowY:'auto', padding:'24px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'18px 16px', alignContent:'start' }}>
                {FIELD_CONFIG[activeTab].map(f => (
                  <div key={f.key} style={{ gridColumn:f.span===2?'1/-1':'auto', display:'flex', flexDirection:'column', gap:5 }}>
                    <label style={{ fontSize:11.5, fontWeight:700, color:C.t2 }}>{f.label}{f.required && <span style={{ color:C.red, marginLeft:2 }}>*</span>}</label>
                    {renderField(f)}
                  </div>
                ))}
              </div>
              <div style={{ padding:'16px 24px', borderTop:`1px solid ${C.borderL}`, display:'flex', gap:10, justifyContent:'flex-end', background:'#FAFBFC', flexShrink:0 }}>
                <button type="button" onClick={closeDrawer} disabled={saving} style={{ height:40, padding:'0 22px', border:`1px solid ${C.border}`, borderRadius:9, background:C.white, color:C.t2, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ height:40, padding:'0 28px', minWidth:140, background:saving?C.navyM:C.gold, color:saving?'#fff':'#0D1B2A', border:'none', borderRadius:9, fontSize:13, fontWeight:800, cursor:saving?'not-allowed':'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:7, boxShadow:'0 2px 10px rgba(245,166,35,0.38)' }}>
                  {saving ? <><Spin/> Saving...</> : <><FiSave size={14}/> {editingItem?'Update':'Create'}</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* LEAD STATUS DRAWER */}
      {statusDrawer && (
        <div style={{ position:'fixed', inset:0, zIndex:2000, display:'flex' }}>
          <div style={{ flex:1, background:'rgba(13,27,42,0.45)', backdropFilter:'blur(3px)' }} onClick={closeStatusDrawer}/>
          <div className="ms-drawer" style={{ width:460, background:C.white, boxShadow:'-8px 0 40px rgba(13,27,42,0.20)', display:'flex', flexDirection:'column', animation:'cfSlideIn 0.24s ease both' }}>
            <div style={{ padding:'18px 24px', background:'linear-gradient(120deg,#92400E,#B45309)', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
              <div>
                <div style={{ fontSize:10.5, fontWeight:700, color:'rgba(255,255,255,0.50)', marginBottom:3, textTransform:'uppercase', letterSpacing:'0.08em' }}>Lead Status</div>
                <div style={{ fontSize:17, fontWeight:800, color:'#fff', letterSpacing:'-0.02em' }}>{statusEdit?'Edit':'Add'} Lead Status</div>
                <div style={{ fontSize:12, color:'rgba(255,255,255,0.60)', marginTop:2 }}>{statusForm.parent?'Sub-status (assigned to a parent)':'Leave parent blank for a top-level status'}</div>
              </div>
              <button onClick={closeStatusDrawer} style={{ width:36, height:36, borderRadius:9, background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.20)', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}><FiX size={17}/></button>
            </div>
            <form onSubmit={handleStatusSave} style={{ flex:1, display:'flex', flexDirection:'column', overflowY:'auto' }}>
              <div style={{ flex:1, padding:'24px', display:'flex', flexDirection:'column', gap:18 }}>
                <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                  <label style={{ fontSize:11.5, fontWeight:700, color:C.t2 }}>Status Name <span style={{ color:C.red }}>*</span></label>
                  <input type="text" value={statusForm.name} onChange={e=>setStatusForm(p=>({...p,name:e.target.value}))} placeholder="e.g. Under Follow, Login, Sanction" style={inputSt()} onFocus={onFocus} onBlur={onBlur} required/>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                  <label style={{ fontSize:11.5, fontWeight:700, color:C.t2 }}>Parent Status <span style={{ fontWeight:400, color:C.t4, marginLeft:6, fontSize:11 }}>(leave blank for top-level)</span></label>
                  <div style={{ position:'relative' }}>
                    <select value={statusForm.parent} onChange={e=>setStatusForm(p=>({...p,parent:e.target.value}))} style={{ ...inputSt(), cursor:'pointer', paddingRight:28 }} onFocus={onFocus} onBlur={onBlur}>
                      <option value="">-- Top-level (Parent) status --</option>
                      {parentStatuses.filter(p=>!statusEdit||p.id!==statusEdit.id).map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', color:C.t4, fontSize:10 }}>&#9662;</span>
                  </div>
                </div>
                {statusForm.name && (
                  <div style={{ background:C.bg, borderRadius:9, padding:'10px 14px', fontSize:12.5, color:C.t2, display:'flex', alignItems:'center', gap:6 }}>
                    <span style={{ color:C.t4 }}>Preview:</span>
                    {statusForm.parent && (<><span style={{ fontWeight:600 }}>{parentStatuses.find(p=>String(p.id)===String(statusForm.parent))?.name}</span><FiChevronRight size={12} style={{ color:C.t4 }}/></>)}
                    <span style={{ background:statusForm.parent?C.purpleL:C.goldL, color:statusForm.parent?C.purple:'#92400E', padding:'2px 10px', borderRadius:20, fontWeight:700, fontSize:12 }}>{statusForm.name}</span>
                  </div>
                )}
              </div>
              <div style={{ padding:'16px 24px', borderTop:`1px solid ${C.borderL}`, display:'flex', gap:10, justifyContent:'flex-end', background:'#FAFBFC', flexShrink:0 }}>
                <button type="button" onClick={closeStatusDrawer} disabled={statusSaving} style={{ height:40, padding:'0 22px', border:`1px solid ${C.border}`, borderRadius:9, background:C.white, color:C.t2, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
                <button type="submit" disabled={statusSaving} style={{ height:40, padding:'0 28px', minWidth:140, background:statusSaving?'#B45309':'#92400E', color:'#fff', border:'none', borderRadius:9, fontSize:13, fontWeight:800, cursor:statusSaving?'not-allowed':'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:7, boxShadow:'0 2px 8px rgba(146,64,14,0.30)' }}>
                  {statusSaving ? <><Spin/> Saving...</> : <><FiSave size={14}/> {statusEdit?'Update':'Create'}</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CONFIRM DELETE */}
      {(confirmDel || statusConfirm) && (() => {
        const item     = confirmDel || statusConfirm;
        const isStatus = !!statusConfirm;
        const close    = () => isStatus ? setStatusConfirm(null) : setConfirmDel(null);
        const doDelete = () => isStatus ? handleStatusDelete(item) : handleDelete(item);
        return (
          <div style={{ position:'fixed', inset:0, background:'rgba(13,27,42,0.55)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2100, padding:16 }}
            onClick={e=>{ if(e.target===e.currentTarget) close(); }}>
            <div style={{ background:C.white, borderRadius:16, width:'100%', maxWidth:400, boxShadow:'0 24px 60px rgba(13,27,42,0.35)', overflow:'hidden', animation:'cfFadeUp 0.18s ease both' }}>
              <div style={{ padding:'18px 22px', borderBottom:`1px solid ${C.borderL}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontWeight:800, fontSize:15, color:C.t1 }}>Confirm Delete</span>
                <button onClick={close} style={{ background:'none', border:'none', cursor:'pointer', color:C.t4, display:'flex' }}><FiX size={18}/></button>
              </div>
              <div style={{ padding:'22px', textAlign:'center' }}>
                <div style={{ width:52, height:52, borderRadius:'50%', background:C.redL, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}><FiTrash2 size={22} style={{ color:C.red }}/></div>
                <p style={{ fontSize:13.5, color:C.t2, margin:'0 0 6px', lineHeight:1.5 }}>Delete <strong style={{ color:C.t1 }}>"{item.name}"</strong>?</p>
                <p style={{ fontSize:12, color:C.t4, margin:0 }}>This action cannot be undone.</p>
              </div>
              <div style={{ padding:'14px 22px', borderTop:`1px solid ${C.borderL}`, display:'flex', gap:10, justifyContent:'flex-end', background:'#FAFBFC' }}>
                <button onClick={close} style={{ height:38, padding:'0 20px', border:`1px solid ${C.border}`, borderRadius:9, background:C.white, color:C.t2, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
                <button onClick={doDelete} style={{ height:38, padding:'0 22px', background:C.red, color:'#fff', border:'none', borderRadius:9, fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:6, boxShadow:'0 2px 8px rgba(220,38,38,0.35)' }}>
                  <FiTrash2 size={13}/> Delete
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
}